import { writeLocation } from "../utils/appLocation";
import Icon from "../components/Icon";
import { Toast, PageTitle, Btn, Card, ConfirmModal, Input, Modal, Pagination, Select, Spinner } from "../components";
import { useEffect, useState, useRef } from "react";
import { apiFetch } from "../api";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
  addPassengerToRouteGroup,
  correctRouteGroupData,
  dispatchRouteGroups,
  finalizeRouteDispatch,
  formatPhoneValidationFalhas,
  formatRouteDispatchError,
  removePassengerFromRouteGroup,
  resendRouteGroupNotifications,
  resendRouteGroupReminders,
} from "../utils/routeFlow";
import { formatTrechoLabel, formatMotoristaResposta, getDestinoLabel, getDocumentoOperacionalLabel } from "../utils/tenantLabels";
import { getOperacionalStatusMeta, isManualRoute, getRoutePedidoCodes } from "../utils/pedidoLabels";
import {
  formatRouteDispatchFailureMessage,
  hasRouteDispatchFailure,
} from "../utils/routeCommunication";
import { useTenant } from "../context/TenantContext";
import { recordUiError } from "../observability";

// A empresa de destino da viagem tem prioridade sobre o rótulo geral da transportadora.
const formatTrecho = (trecho, config, destino) => {
  const routeConfig = destino ? { ...config, destino_label: destino } : config;
  return formatTrechoLabel(trecho, routeConfig) || (trecho === "volta" ? "Volta → Residências" : `Ida → ${getDestinoLabel(routeConfig)}`);
};

function formatPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function phoneToDisplay(stored) {
  const d = (stored || "").replace(/\D/g, "");
  const local = d.startsWith("55") ? d.slice(2) : d;
  return formatPhone(local);
}

const PER_PAGE = 10;
const PROGRAMACAO_MODE_EXISTING = "existing";
const PROGRAMACAO_MODE_CREATE = "create";

const collectRouteProgramacoes = (route) => {
  const map = new Map();
  for (const group of route?.payload?.grupos || []) {
    if (group.programacao_responsavel_id) {
      map.set(
        Number(group.programacao_responsavel_id),
        group.programacao_codigo_responsavel || `#${group.programacao_responsavel_id}`
      );
    }
    for (const person of group.pessoas || []) {
      if (person.programacao_id) {
        map.set(Number(person.programacao_id), person.id_pedido_origem || `#${person.programacao_id}`);
      }
    }
  }
  return [...map.entries()].map(([id, codigo]) => ({ id, codigo }));
};

const BACKLOG_STATUS_META = {
  rascunho: {
    label: "Aguardando envio",
    bg: "rgba(59,130,246,0.12)",
    color: "#93c5fd",
  },
  enviada: {
    label: "Enviado ao motorista",
    bg: "rgba(34,197,94,0.12)",
    color: "#86efac",
  },
};

const getBacklogStatusMeta = (route) => (
  hasRouteDispatchFailure(route)
    ? {
      label: "Falha no envio",
      bg: "rgba(239,68,68,0.12)",
      color: "#fca5a5",
    }
    : (BACKLOG_STATUS_META[route?.status] || BACKLOG_STATUS_META.rascunho)
);

const getMotoristaResponseMeta = (route) => {
  const total = Math.max(
    Number(route?.total_grupos) || 0,
    Array.isArray(route?.payload?.grupos) ? route.payload.grupos.length : 0
  );
  const responded = Math.max(0, Number(route?.total_motoristas_responderam) || 0);
  const expired = Math.max(0, Number(route?.total_motoristas_resposta_expirada) || 0);
  if (!route?.tem_envio_iniciado) {
    return { label: "Não enviado", color: "#a1a1aa", bg: "rgba(113,113,122,0.15)" };
  }
  if (hasRouteDispatchFailure(route)) {
    return { label: "Mensagem não entregue", color: "#fca5a5", bg: "rgba(239,68,68,0.12)" };
  }
  if (expired > 0 || String(route?.status_operacional || "") === "motorista_nao_respondeu") {
    return {
      label: responded > 0 && total > 0
        ? `${expired || 1} sem resposta · ${responded}/${total} responderam`
        : `${expired || 1} sem resposta no prazo`,
      color: "#fca5a5",
      bg: "rgba(239,68,68,0.12)",
    };
  }
  if (String(route?.status_operacional || "") === "falha") {
    return { label: "Falha no envio", color: "#fca5a5", bg: "rgba(239,68,68,0.12)" };
  }
  if (responded > 0 && total > 0 && responded >= total) {
    return { label: "Todos responderam", color: "#86efac", bg: "rgba(34,197,94,0.12)" };
  }
  if (responded > 0) {
    return {
      label: total > 0 ? `${responded}/${total} responderam` : `${responded} responderam`,
      color: "#fde68a",
      bg: "rgba(234,179,8,0.12)",
    };
  }
  return { label: "Aguardando resposta", color: "#fde68a", bg: "rgba(234,179,8,0.12)" };
};

const canStartRouteDispatch = (route) => (
  route?.status === "rascunho" && !route?.tem_envio_iniciado
);

const canRetryRouteDispatch = (route) => (
  route?.status === "rascunho"
  && !route?.tem_resposta_motorista
  && hasRouteDispatchFailure(route)
);

const OperationalBadge = ({ status, style }) => {
  const m = getOperacionalStatusMeta(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 999, background: m.bg, color: m.color, fontSize: '0.875rem', fontWeight: 700, ...style }}>
      {m.label}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).substring(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const normalizeDateKey = (value) => {
  if (!value) return "";
  const normalized = String(value).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : "";
};
const parseDateKey = (value) => {
  const normalized = normalizeDateKey(value);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};
const addDays = (date, amount) => {
  const base = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  base.setDate(base.getDate() + amount);
  return base;
};
const formatCalendarMonth = (date) => date.toLocaleDateString("pt-BR", { month: "long" }).toUpperCase();
const formatCalendarWeekday = (date) => date.toLocaleDateString("pt-BR", { weekday: "long" }).toUpperCase();
const formatShortWeekday = (date) => date.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "").toUpperCase();

const getRouteMapLinks = (route) => {
  const linksByGroup = new Map();
  for (const event of route?.eventos || []) {
    const link = String(event?.detalhes?.link_mapa || "").trim();
    if (!link) continue;
    const groupIndex = Number(event?.detalhes?.grupo_index);
    const key = Number.isInteger(groupIndex) ? `grupo-${groupIndex}` : `link-${link}`;
    linksByGroup.set(key, {
      grupo_index: Number.isInteger(groupIndex) ? groupIndex : null,
      link_mapa: link,
      atualizado_em: event.criado_em || null,
    });
  }
  for (const map of route?.mapas_gerados || []) {
    const link = String(map?.link_mapa || "").trim();
    if (!link) continue;
    const groupIndex = Number(map.grupo_index);
    const key = Number.isInteger(groupIndex) ? `grupo-${groupIndex}` : `link-${link}`;
    linksByGroup.set(key, {
      grupo_index: Number.isInteger(groupIndex) ? groupIndex : null,
      link_mapa: link,
      atualizado_em: map.atualizado_em || null,
    });
  }
  const uniqueByLink = new Map();
  for (const map of linksByGroup.values()) {
    const existing = uniqueByLink.get(map.link_mapa);
    if (!existing || (existing.grupo_index === null && map.grupo_index !== null)) {
      uniqueByLink.set(map.link_mapa, map);
    }
  }
  return [...uniqueByLink.values()].sort(
    (left, right) => (left.grupo_index ?? Number.MAX_SAFE_INTEGER) - (right.grupo_index ?? Number.MAX_SAFE_INTEGER)
  );
};

const getPersistedMapsWithoutTimelineEvent = (route) => {
  const timelineLinks = new Set(
    (route?.eventos || [])
      .map((event) => String(event?.detalhes?.link_mapa || "").trim())
      .filter(Boolean)
  );
  return (route?.mapas_gerados || []).filter((map) => {
    const link = String(map?.link_mapa || "").trim();
    return link && !timelineLinks.has(link);
  });
};

const formatMotoristaRespostaLabel = (resposta, config) => formatMotoristaResposta(resposta, config);

const countDuplicateMotoristaRespondeuEvents = (eventos = []) => {
  const seen = new Set();
  let duplicates = 0;
  for (const event of eventos) {
    if (event?.tipo !== "motorista_respondeu") continue;
    const telefone = String(event?.detalhes?.motorista_telefone || "").replace(/\D/g, "");
    const key = telefone || `event-${event.id}`;
    if (seen.has(key)) duplicates += 1;
    else seen.add(key);
  }
  return duplicates;
};

const formatEventFailure = (failure) => {
  if (!failure || typeof failure !== "object") return "";
  const nome = String(failure.funcionario_nome || failure.nome || "").trim();
  const phone = String(failure.telefone || failure.number || "").trim();
  const rawMessage = String(failure.erro || failure.message || failure.mensagem || "").trim();
  const message = rawMessage.includes('"exists":false')
    ? "número não está no WhatsApp ou está incorreto"
    : rawMessage.length > 120
      ? "falha ao enviar pelo WhatsApp"
      : rawMessage;
  if (nome && phone && message) return `${nome} (${phone}) — ${message}`;
  if (nome && message) return `${nome} — ${message}`;
  if (!phone && !message) return "";
  if (!phone) return message;
  if (!message) return phone;
  return `${phone} — ${message}`;
};

const EVENT_REASON_LABELS = {
  dados_incompletos: "faltam dados da rota ou do passageiro",
  grupo_nao_disparado: "a van ainda não havia sido disparada",
  grupo_nao_disponivel: "a van não estava mais disponível para recálculo",
  google_api_key_ausente: "a integração do Google Maps não está configurada",
  motorista_em_rota: "o motorista já havia iniciado a rota",
  motorista_iniciou_durante_processamento: "o motorista iniciou a rota durante o recálculo",
  rota_indisponivel: "a rota não estava mais disponível",
  sem_telefone_motorista: "o motorista está sem telefone válido",
  sem_destinatarios: "não havia destinatários válidos para a atualização",
  notificacao_nao_enviada: "o provedor não confirmou o enfileiramento da atualização",
};

const EVENT_TYPE_LABELS = {
  funcionario_recusou_viagem: "Passageiro recusou a viagem",
  rota_atualizada_recusa: "Rota recalculada após recusa",
  documento_km_final_criado: "Link de finalização preparado",
  documento_km_final_enviado: "Link de finalização enviado",
  documento_km_final_falhou: "Falha no link de finalização",
  lembrete_assinatura_enviado: "Lembrete manual enviado",
  lembrete_auto_funcionario_inicio_enviado: "Lembrete automático de passageiro",
  lembrete_auto_km_final_enviado: "Lembrete automático de finalização",
  motorista_resposta_expirada: "Prazo de resposta do motorista expirou",
  rota_finalizada: "Rota finalizada",
  rota_dados_corrigidos_pos_disparo: "Dados da rota corrigidos",
};

const REMINDER_TYPE_LABELS = {
  funcionario: "Confirmação do passageiro",
  motorista_inicio: "Início da rota pelo motorista",
  motorista_km_final: "Finalização/KM final pelo motorista",
};

const formatEventReason = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return EVENT_REASON_LABELS[raw] || raw.replaceAll("_", " ");
};

const optionalNumber = (value) => (
  value === null || value === undefined || value === "" ? Number.NaN : Number(value)
);

const renderEventDetails = (details) => {
  if (!details || typeof details !== "object" || Array.isArray(details)) return null;

  const totalDestinatarios = optionalNumber(details.total_destinatarios);
  const totalSucessos = optionalNumber(details.total_sucessos);
  const totalFalhas = optionalNumber(details.total_falhas);
  const failureRows = Array.isArray(details.falhas)
    ? details.falhas.map(formatEventFailure).filter(Boolean)
    : [];
  const routeLink = String(details.link_mapa || "").trim();
  const refusalName = String(details.funcionario_nome || "").trim();
  const motoristaName = String(details.motorista_nome || "").trim();
  const refusalReason = String(details.observacao_recusa || "").trim();
  const correctionBefore = details.antes && typeof details.antes === "object" ? details.antes : null;
  const correctionAfter = details.depois && typeof details.depois === "object" ? details.depois : null;
  const isCorrection = Boolean(correctionBefore && correctionAfter);
  const errorMessage = formatEventReason(
    details.erro || details.erro_mensagem || details.notificacao_erro || (!isCorrection ? details.motivo : "")
  );
  const totalLinks = optionalNumber(details.total_links);
  const totalStops = optionalNumber(details.total_paradas);
  const durationMinutes = optionalNumber(details.duracao_minutos);
  const groupIndex = optionalNumber(details.grupo_index);
  const responseDeadline = String(details.prazo_resposta_em || "").trim();
  const passengersWithoutLink = optionalNumber(details.passageiros_sem_link);
  const notifiedPassengers = optionalNumber(details.passageiros_notificados);
  const reminderRows = Array.isArray(details.lembretes)
    ? details.lembretes.map((lembrete) => ({
      key: `${lembrete?.tipo || "lembrete"}-${lembrete?.assinatura_id || "sem-id"}`,
      tipo: REMINDER_TYPE_LABELS[lembrete?.tipo] || String(lembrete?.tipo || "Lembrete").replaceAll("_", " "),
      nome: String(lembrete?.destinatario_nome || "").trim(),
    }))
    : [];

  if (
    !routeLink
    && !refusalName
    && !motoristaName
    && !refusalReason
    && !errorMessage
    && !Number.isFinite(totalDestinatarios)
    && !Number.isFinite(totalLinks)
    && !Number.isFinite(totalStops)
    && !Number.isFinite(durationMinutes)
    && !responseDeadline
    && !Number.isFinite(passengersWithoutLink)
    && !Number.isFinite(notifiedPassengers)
    && !failureRows.length
    && !reminderRows.length
    && !details.tentativa
    && !details.status_comunicacao
    && !isCorrection
  ) {
    return null;
  }

  return (
    <div style={{ display: "grid", gap: 8, marginTop: 4 }}>
      {routeLink && (
        <a
          href={routeLink}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "fit-content",
            padding: "7px 12px",
            borderRadius: 10,
            border: "1px solid rgba(249,115,22,0.35)",
            background: "rgba(249,115,22,0.10)",
            color: "#fdba74",
            fontSize: '0.875rem',
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          Abrir rota no mapa
        </a>
      )}
      {Number.isInteger(groupIndex) && groupIndex >= 0 && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          Van: {groupIndex + 1}
        </div>
      )}
      {isCorrection && (
        <div style={{ display: "grid", gap: 4, padding: "8px 10px", borderRadius: 8, background: "rgba(59,130,246,0.10)", color: "#bfdbfe", fontSize: '0.875rem' }}>
          {correctionBefore.horario !== correctionAfter.horario && (
            <div>Horário: <strong>{correctionBefore.horario || "—"}</strong> → <strong>{correctionAfter.horario || "—"}</strong></div>
          )}
          {correctionBefore.programacao_codigo !== correctionAfter.programacao_codigo && (
            <div>PG: <strong>{correctionBefore.programacao_codigo || "—"}</strong> → <strong>{correctionAfter.programacao_codigo || "—"}</strong></div>
          )}
          <div>Motivo: {details.motivo || "—"}</div>
          <div>Alterado por: {details.usuario_nome || `Usuário #${details.usuario_id || "—"}`} ({details.usuario_role || "perfil não informado"})</div>
          <div>WhatsApp/Meta: nenhuma mensagem enviada</div>
        </div>
      )}
      {Number.isFinite(totalLinks) && totalLinks > 0 && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          Links da rota: {totalLinks}
        </div>
      )}
      {(Number.isFinite(totalStops) || Number.isFinite(durationMinutes)) && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          {Number.isFinite(totalStops) ? `${totalStops} parada(s)` : ""}
          {Number.isFinite(totalStops) && Number.isFinite(durationMinutes) ? " · " : ""}
          {Number.isFinite(durationMinutes) ? `${durationMinutes} min estimados` : ""}
        </div>
      )}
      {errorMessage && (
        <div style={{ fontSize: '0.875rem', color: "#fca5a5" }}>
          Problema: {errorMessage}
        </div>
      )}
      {(refusalName || refusalReason) && (
        <div style={{ fontSize: '0.875rem', color: "#fbbf24" }}>
          {refusalName && <div>Passageiro: {refusalName}</div>}
          {refusalReason && <div>Motivo informado: {refusalReason}</div>}
        </div>
      )}
      {motoristaName && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          Motorista: {motoristaName}
        </div>
      )}
      {responseDeadline && (
        <div style={{ fontSize: '0.875rem', color: "#fbbf24" }}>
          Prazo da resposta: {formatDateTime(responseDeadline)}
        </div>
      )}
      {(Number.isFinite(passengersWithoutLink) || Number.isFinite(notifiedPassengers)) && (
        <div style={{ fontSize: '0.875rem', color: "#fca5a5" }}>
          Passageiros notificados: {Number.isFinite(notifiedPassengers) ? notifiedPassengers : 0}
          {Number.isFinite(passengersWithoutLink) ? ` · ${passengersWithoutLink} sem link de assinatura` : ""}
        </div>
      )}
      {details.tentativa && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          Tentativa automática #{details.tentativa}
        </div>
      )}
      {details.status_comunicacao && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          Comunicação: {String(details.status_comunicacao).replaceAll("_", " ")}
          {details.canal ? ` · canal ${details.canal}` : ""}
        </div>
      )}
      {reminderRows.length > 0 && (
        <div style={{ display: "grid", gap: 3, fontSize: '0.875rem' }}>
          <strong>Destinatário{reminderRows.length !== 1 ? "s" : ""}:</strong>
          {reminderRows.map((row) => (
            <div key={row.key} style={{ color: "var(--muted)" }}>
              • {row.tipo}{row.nome ? ` — ${row.nome}` : ""}
            </div>
          ))}
        </div>
      )}
      {Number.isFinite(totalDestinatarios) && (
        <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
          Envios: {Number.isFinite(totalSucessos) ? totalSucessos : 0}/{totalDestinatarios} com sucesso
          {Number.isFinite(totalFalhas) && totalFalhas > 0 ? ` · ${totalFalhas} falha(s)` : ""}
        </div>
      )}
      {failureRows.length > 0 && (
        <div style={{ fontSize: '0.875rem', color: "#fca5a5" }}>
          Falhas: {failureRows.slice(0, 5).join(" · ")}
          {failureRows.length > 5 ? ` · +${failureRows.length - 5} restante(s)` : ""}
        </div>
      )}
    </div>
  );
};

export default function BacklogRotasPage({
  onEditRoute,
  onOpenPlanning,
  onOpenDocument = null,
  initialRouteId = null,
  onInitialRouteOpened,
  embedded = false,
  sharedDateKey = null,
  onSharedDateChange = null,
  sharedCalendarAnchor = null,
  onSharedCalendarAnchorChange = null,
  externalSearchQuery = null,
}) {
  const { config } = useTenant();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [routes, setRoutes] = useState([]);
  const [totalRoutes, setTotalRoutes] = useState(0);
  const [countsByDate, setCountsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const routeOpening = useRef(0);
  useEffect(() => () => { routeOpening.current += 1; }, []);
  const [rowLoadingId, setRowLoadingId] = useState(null);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const skipSearchDebounceRef = useRef(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedDateKey, setSelectedDateKey] = useState(() => buildDateKey(new Date()));
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [page, setPage] = useState(1);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [phoneValidationDialog, setPhoneValidationDialog] = useState(null);
  const [addPassengerDialog, setAddPassengerDialog] = useState(null);
  const [removePassengerDialog, setRemovePassengerDialog] = useState(null);
  const [correctionDialog, setCorrectionDialog] = useState(null);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionariosLoading, setFuncionariosLoading] = useState(false);
  const [selectedFuncionarioIds, setSelectedFuncionarioIds] = useState(() => new Set());
  const [passengerSearch, setPassengerSearch] = useState("");
  const [programacaoMode, setProgramacaoMode] = useState(PROGRAMACAO_MODE_EXISTING);
  const [programacaoSearch, setProgramacaoSearch] = useState("");
  const [programacoesDisponiveis, setProgramacoesDisponiveis] = useState([]);
  const [selectedProgramacaoId, setSelectedProgramacaoId] = useState("");
  const [newProgramacaoCodigo, setNewProgramacaoCodigo] = useState("");
  const [newProgramacaoData, setNewProgramacaoData] = useState("");
  const [newProgramacaoHorario, setNewProgramacaoHorario] = useState("");
  const [confirmNewProgramacaoLink, setConfirmNewProgramacaoLink] = useState(false);
  const [motoristas, setMotoristas] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [executorDialog, setExecutorDialog] = useState(null);
  const [savingExecutor, setSavingExecutor] = useState(false);
  const [refreshingSelectedRoute, setRefreshingSelectedRoute] = useState(false);
  const selectedRouteMapLinks = getRouteMapLinks(selectedRoute);
  const persistedMapsWithoutTimelineEvent = getPersistedMapsWithoutTimelineEvent(selectedRoute);
  const docOperacionalLabel = getDocumentoOperacionalLabel(config);
  const duplicateMotoristaRespostas = countDuplicateMotoristaRespondeuEvents(selectedRoute?.eventos);
  const canEditAwaitingMotorista = (route) => (
    !route?.tem_resposta_motorista
    && (route?.status === "rascunho" || route?.status === "enviada")
  );
  const canEditDraft = canEditAwaitingMotorista;
  const canCancelProgramPlan = (route) => (
    canEditAwaitingMotorista(route) && Boolean(route?.lote_operacional_id)
  );
  const hasDispatchStarted = (route) => (
    Boolean(route?.tem_envio_iniciado) || route?.status === "enviada"
  );
  const requiresDeleteConfirmationText = (route) => (
    hasDispatchStarted(route) || !canCancelProgramPlan(route)
  );
  const canDeleteRoute = (route) => Boolean(route?.id);
  const isDeleteAfterResponse = (route) => Boolean(route?.tem_resposta_motorista);
  const isProgramDeleteAfterResponse = (route) => (
    isDeleteAfterResponse(route) && Boolean(route?.lote_operacional_id)
  );
  const showToast = (msg) => {
    setToast({ message: msg, type: "success" });
  };
  const showError = (msg) => {
    recordUiError({ message: msg, page: "pedidos_operacao", source: "toast" });
    setToast({ message: msg, type: "error" });
  };

  const isDateControlled = embedded && onSharedDateChange;
  const isSearchControlled = embedded && externalSearchQuery !== null;
  const effectiveDateKey = isDateControlled ? (sharedDateKey ?? "") : selectedDateKey;
  const effectiveSearchQuery = isSearchControlled ? externalSearchQuery : searchQuery;
  const setEffectiveDateKey = (dateKey) => {
    if (isDateControlled) onSharedDateChange(dateKey);
    else setSelectedDateKey(dateKey);
  };

  const loadRoutes = async (targetPage = page, query = effectiveSearchQuery) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(PER_PAGE),
      });
      if (query.trim()) params.set("q", query.trim());
      if (statusFilter) params.set("status", statusFilter);
      if (effectiveDateKey) params.set("data", effectiveDateKey);

      const data = await apiFetch(`/rotas?${params.toString()}`);
      if (Array.isArray(data)) {
        setRoutes(data);
        setTotalRoutes(data.length);
        setCountsByDate({});
      } else {
        setRoutes(Array.isArray(data?.items) ? data.items : []);
        setTotalRoutes(Number(data?.total) || 0);
        setCountsByDate(data?.counts_by_date && typeof data.counts_by_date === "object" ? data.counts_by_date : {});
      }
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchDebounceRef.current) {
      skipSearchDebounceRef.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearchQuery(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadRoutes(page, effectiveSearchQuery);
  }, [page, effectiveSearchQuery, statusFilter, effectiveDateKey]);

  useEffect(() => {
    if (!initialRouteId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const details = await apiFetch(`/rotas/${initialRouteId}`);
        if (!cancelled) setSelectedRoute(details);
      } catch (e) {
        if (!cancelled) showError(e.message);
      } finally {
        if (!cancelled) onInitialRouteOpened?.();
      }
    })();
    return () => { cancelled = true; };
  }, [initialRouteId]);

  useEffect(() => {
    const routeId = Number(selectedRoute?.id);
    if (!routeId) return undefined;

    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const details = await apiFetch(`/rotas/${routeId}`);
        if (!cancelled) {
          setSelectedRoute((current) => (
            Number(current?.id) === routeId ? details : current
          ));
        }
      } catch {
        // A atualização manual continua disponível; uma falha transitória do
        // polling não deve fechar a tela nem esconder os dados já carregados.
      }
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedRoute?.id]);

  useEffect(() => {
    if (isDateControlled || calendarAnchor || !Object.keys(countsByDate).length) return;
    const availableDates = Object.keys(countsByDate).sort();
    const todayKey = buildDateKey(new Date());
    const firstUpcoming = availableDates.find((dateKey) => dateKey >= todayKey);
    const anchorDate = parseDateKey(firstUpcoming || availableDates[0]) || new Date();
    setCalendarAnchor(anchorDate);
  }, [countsByDate, calendarAnchor, isDateControlled]);

  const routeCountsByDate = countsByDate;

  const visibleCalendarAnchor = (isDateControlled ? sharedCalendarAnchor : calendarAnchor) || new Date();
  const calendarDayCount = isMobile ? 5 : 13;
  const calendarDays = Array.from({ length: calendarDayCount }, (_, index) => addDays(visibleCalendarAnchor, index));

  const openConfirmRouteDialog = async (route) => {
    setRowLoadingId(route.id);
    try {
      const fullRoute = route?.payload?.grupos?.length
        ? route
        : await apiFetch(`/rotas/${route.id}`);
      setConfirmDialog({
        type: "confirmRoute",
        route: fullRoute,
        retry: Boolean(route?.retry_dispatch),
      });
    } catch (e) {
      showError(e.message);
    } finally {
      setRowLoadingId(null);
    }
  };

  const openRoute = async (route) => {
    const opening = ++routeOpening.current;
    try {
      const details = await apiFetch(`/rotas/${route.id}`);
      if (opening !== routeOpening.current) return;
      setSelectedRoute(details);
      if (embedded) writeLocation({ rota: details.id });
    } catch (e) {
      showError(e.message);
    }
  };

  const closeRoute = () => { routeOpening.current += 1; setSelectedRoute(null); if (embedded) writeLocation({ rota: null }, { replace: true }); };

  const refreshSelectedRoute = async () => {
    const routeId = Number(selectedRoute?.id);
    if (!routeId) return;
    setRefreshingSelectedRoute(true);
    try {
      const details = await apiFetch(`/rotas/${routeId}`);
      setSelectedRoute((current) => (
        Number(current?.id) === routeId ? details : current
      ));
    } catch (e) {
      showError(e.message);
    } finally {
      setRefreshingSelectedRoute(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([apiFetch("/motoristas"), apiFetch("/veiculos")])
      .then(([motoristasData, veiculosData]) => {
        if (cancelled) return;
        setMotoristas(Array.isArray(motoristasData) ? motoristasData : []);
        setVeiculos(Array.isArray(veiculosData) ? veiculosData : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const openExecutorDialog = (route, group, groupIndex) => {
    setExecutorDialog({
      route,
      groupIndex,
      motoristaId: String(group?.motorista_executor_id || group?.motorista?.id || ""),
      veiculoId: String(group?.veiculo_executor_id || group?.veiculo?.id || ""),
      responsavelCodigo: group?.programacao_codigo_responsavel || null,
    });
  };

  const openCorrectionDialog = (route, group, groupIndex) => {
    const groupProgramacaoIds = new Set([
      Number(group?.programacao_responsavel_id),
      ...(group?.pessoas || []).map((pessoa) => Number(pessoa?.programacao_id)),
    ].filter((id) => Number.isInteger(id) && id > 0));
    const programacoes = collectRouteProgramacoes(route).filter(
      (programacao) => groupProgramacaoIds.has(Number(programacao.id))
    );
    const programacaoId = Number(group?.programacao_responsavel_id)
      || programacoes[0]?.id
      || null;
    const programacaoCodigo = programacoes.find((item) => Number(item.id) === Number(programacaoId))?.codigo
      || group?.programacao_codigo_responsavel
      || route?.id_pedido
      || "";
    setCorrectionDialog({
      route,
      group,
      groupIndex,
      programacoes,
      programacaoId: programacaoId ? String(programacaoId) : "",
      programacaoCodigo,
      horario: group?.horario || route?.horario || "",
      motivo: "",
      confirmed: false,
    });
  };

  const saveRouteCorrection = async () => {
    if (!correctionDialog) return;
    const horario = String(correctionDialog.horario || "").trim();
    const programacaoCodigo = String(correctionDialog.programacaoCodigo || "").trim();
    const motivo = String(correctionDialog.motivo || "").trim();
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(horario)) {
      showError("Informe um horário válido.");
      return;
    }
    if (!programacaoCodigo) {
      showError("Informe o número da PG.");
      return;
    }
    if (motivo.length < 5) {
      showError("Informe o motivo da correção com pelo menos 5 caracteres.");
      return;
    }
    if (!correctionDialog.confirmed) {
      showError("Confirme que a alteração será somente interna no RotaFlex.");
      return;
    }

    setSavingCorrection(true);
    setRowLoadingId(correctionDialog.route.id);
    try {
      const result = await correctRouteGroupData({
        routeId: correctionDialog.route.id,
        groupIndex: correctionDialog.groupIndex,
        horario,
        programacaoId: correctionDialog.programacaoId
          ? Number(correctionDialog.programacaoId)
          : null,
        programacaoCodigo,
        motivo,
        confirmarCorrecao: true,
      });
      const updated = await apiFetch(`/rotas/${correctionDialog.route.id}`);
      setSelectedRoute(updated);
      setCorrectionDialog(null);
      await loadRoutes(page, searchQuery);
      showToast(result?.correcao?.duplicidade_codigo_temporaria
        ? "Dados corrigidos. O número repetido foi liberado temporariamente e as PGs continuam separadas. Nenhuma mensagem foi enviada pela Meta."
        : "Dados corrigidos no RotaFlex. Nenhuma mensagem foi enviada pela Meta.");
    } catch (e) {
      showError(e.message);
    } finally {
      setSavingCorrection(false);
      setRowLoadingId(null);
    }
  };

  const saveExecutor = async () => {
    if (!executorDialog) return;
    const motoristaId = Number(executorDialog.motoristaId);
    const veiculoId = Number(executorDialog.veiculoId);
    if (!motoristaId || !veiculoId) {
      showError("Escolha o motorista e o veículo desta van.");
      return;
    }

    setSavingExecutor(true);
    try {
      await apiFetch(
        `/rotas/${executorDialog.route.id}/grupos/${executorDialog.groupIndex}/executor`,
        {
          method: "PATCH",
          body: JSON.stringify({ motorista_id: motoristaId, veiculo_id: veiculoId }),
        }
      );
      const details = await apiFetch(`/rotas/${executorDialog.route.id}`);
      setSelectedRoute(details);
      setRoutes((current) => current.map((row) => (row.id === details.id ? { ...row, ...details } : row)));
      setExecutorDialog(null);
      showToast("Executor da van atualizado.");
    } catch (e) {
      showError(e.message);
    } finally {
      setSavingExecutor(false);
    }
  };

  const openOperationalDocument = (documentoId) => {
    if (!documentoId) return;
    if (typeof onOpenDocument === "function") {
      onOpenDocument(documentoId);
      return;
    }
    showError("Abra a van pela tela de Pedidos para navegar ao documento operacional.");
  };

  const getEnvioGrupo = (route, groupIndex) =>
    (route?.envios_grupos || []).find((envio) => Number(envio.grupo_index) === Number(groupIndex)) || null;

  const openAddPassengerDialog = async (route, group, groupIndex) => {
    setAddPassengerDialog({ route, group, groupIndex });
    setSelectedFuncionarioIds(new Set());
    setPassengerSearch("");
    setProgramacaoMode(PROGRAMACAO_MODE_EXISTING);
    setProgramacaoSearch("");
    setProgramacoesDisponiveis([]);
    setSelectedProgramacaoId(String(group?.programacao_responsavel_id || ""));
    setNewProgramacaoCodigo("");
    setNewProgramacaoData(normalizeDateKey(group?.data || route?.data));
    setNewProgramacaoHorario(String(group?.horario || route?.horario || "").substring(0, 5));
    setConfirmNewProgramacaoLink(false);
    setFuncionariosLoading(true);
    try {
      const params = new URLSearchParams({
        data: normalizeDateKey(group?.data || route?.data),
        trecho: String(group?.trecho || route?.trecho || ""),
        status: "rascunho",
        limit: "100",
      });
      const empresaDestinoId = Number(route?.payload?.empresa_destino_id) || null;
      if (empresaDestinoId) params.set("empresa_destino_id", String(empresaDestinoId));
      const [funcionariosData, programacoesData] = await Promise.all([
        apiFetch("/funcionarios"),
        route?.lote_operacional_id
          ? apiFetch(`/programacoes?${params.toString()}`)
          : Promise.resolve({ items: [] }),
      ]);
      setFuncionarios(Array.isArray(funcionariosData) ? funcionariosData : []);
      setProgramacoesDisponiveis(Array.isArray(programacoesData?.items) ? programacoesData.items : []);
    } catch (e) {
      showError(e.message);
      setAddPassengerDialog(null);
    } finally {
      setFuncionariosLoading(false);
    }
  };

  const togglePassengerSelection = (funcionarioId) => {
    setSelectedFuncionarioIds((current) => {
      const next = new Set(current);
      const id = Number(funcionarioId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmAddPassenger = async () => {
    if (!addPassengerDialog) return;
    const { route, groupIndex } = addPassengerDialog;
    const funcionarioIds = [...selectedFuncionarioIds];
    const isNewProgramacao = programacaoMode === PROGRAMACAO_MODE_CREATE;
    const routeProgramacaoIds = new Set(collectRouteProgramacoes(route).map((item) => Number(item.id)));
    const isExistingProgramacaoUnlinked = !isNewProgramacao
      && Boolean(selectedProgramacaoId)
      && !routeProgramacaoIds.has(Number(selectedProgramacaoId));
    if (!funcionarioIds.length) {
      showError("Selecione ao menos um funcionário.");
      return;
    }
    if (route.lote_operacional_id && !isNewProgramacao && !selectedProgramacaoId) {
      showError("Selecione uma PG existente.");
      return;
    }
    if (isNewProgramacao && !newProgramacaoCodigo.trim()) {
      showError("Informe o número da nova PG.");
      return;
    }
    if (isNewProgramacao && (!newProgramacaoData || !newProgramacaoHorario)) {
      showError("Informe a data e o horário da nova PG.");
      return;
    }
    if ((isNewProgramacao || isExistingProgramacaoUnlinked) && !confirmNewProgramacaoLink) {
      showError("Confirme a criação ou o vínculo da PG com esta rota disparada.");
      return;
    }

    setRowLoadingId(route.id);
    try {
      const result = await addPassengerToRouteGroup({
        routeId: route.id,
        groupIndex,
        funcionarioIds,
        programacaoId: route.lote_operacional_id && !isNewProgramacao
          ? Number(selectedProgramacaoId)
          : null,
        programacaoCodigo: isNewProgramacao ? newProgramacaoCodigo : "",
        criarProgramacao: isNewProgramacao,
        programacaoData: isNewProgramacao ? newProgramacaoData : "",
        programacaoHorario: isNewProgramacao ? newProgramacaoHorario : "",
        confirmarVinculoProgramacao: (isNewProgramacao || isExistingProgramacaoUnlinked)
          && confirmNewProgramacaoLink,
      });
      const updated = await apiFetch(`/rotas/${route.id}`);
      setSelectedRoute(updated);
      setAddPassengerDialog(null);
      await loadRoutes(page, searchQuery);

      const total = funcionarioIds.length;
      const pgVinculada = Boolean(result?.programacao?.vinculada_ao_lote);
      const pgCriada = Boolean(result?.programacao?.criada_inline);
      const labelBase = total === 1 ? "Passageiro adicionado" : `${total} passageiros adicionados`;
      const label = pgCriada
        ? `PG ${result.programacao.codigo} criada e ${labelBase.toLowerCase()}`
        : pgVinculada
        ? `PG ${result.programacao.codigo} vinculada e ${labelBase.toLowerCase()}`
        : labelBase;
      if (result?.aguardando_resposta_motorista || result?.aguardando_primeiro_mapa) {
        showToast(`${label}. O primeiro mapa do motorista já será gerado com os novos endereços.`);
      } else if (result?.notificacao?.skipped) {
        const motivo = result.notificacao.erro || result.notificacao.motivo || "webhook indisponível";
        showToast(`${label} e rota recalculada. WhatsApp não foi disparado: ${motivo}.`);
      } else {
        showToast(`${label}, rota recalculada e mensagens enviadas.`);
      }
    } catch (e) {
      showError(e.message);
    } finally {
      setRowLoadingId(null);
    }
  };

  const confirmRemovePassenger = async () => {
    if (!removePassengerDialog) return;
    const { route, groupIndex, person } = removePassengerDialog;
    setRowLoadingId(route.id);
    try {
      const result = await removePassengerFromRouteGroup({
        routeId: route.id,
        groupIndex,
        funcionarioId: person.id,
      });
      const updated = await apiFetch(`/rotas/${route.id}`);
      setSelectedRoute(updated);
      setRemovePassengerDialog(null);
      await loadRoutes(page, searchQuery);

      if (result?.aguardando_resposta_motorista || result?.aguardando_primeiro_mapa) {
        showToast("Passageiro removido. O primeiro mapa do motorista já será gerado sem esse endereço.");
      } else if (result?.notificacao?.skipped) {
        const motivo = result.notificacao.erro || result.notificacao.motivo || "canal indisponível";
        showToast(`Passageiro removido e rota recalculada. Motorista não notificado: ${motivo}.`);
      } else {
        showToast("Passageiro removido, rota recalculada e motorista notificado.");
      }
    } catch (e) {
      showError(e.message);
    } finally {
      setRowLoadingId(null);
    }
  };

  const confirmResendNotifications = async (route, groupIndex) => {
    setRowLoadingId(route.id);
    try {
      const result = await resendRouteGroupNotifications({
        routeId: route.id,
        groupIndex,
      });
      const queued = Number(result?.notificacao?.queued) || 0;
      const alreadyProcessed = Number(result?.notificacao?.already_processed) || 0;
      const failures = Array.isArray(result?.notificacao?.falhas)
        ? result.notificacao.falhas
        : [];
      if (result?.notificacao?.channel === "legacy" && !result.notificacao.skipped) {
        showToast(`Notificações reenviadas (${result?.total_mensagens || 0} mensagem(ns)).`);
      } else if (result?.notificacao?.skipped || (queued === 0 && failures.length > 0)) {
        const motivo = result.notificacao.erro
          || result.notificacao.motivo
          || failures[0]?.erro
          || "nenhum destinatário pôde ser enfileirado";
        showError(`Notificações não reenviadas: ${motivo}`);
      } else if (failures.length > 0) {
        showError(`${queued} notificação(ões) enfileirada(s), mas ${failures.length} destinatário(s) falharam. Confira os telefones na Central de notificações.`);
      } else if (queued > 0) {
        showToast(`${queued} notificação(ões) enfileirada(s) para um novo envio.`);
      } else if (alreadyProcessed > 0) {
        showToast("Este mesmo pedido de reenvio já havia sido processado; nenhuma mensagem foi duplicada.");
      } else {
        showError("Nenhuma notificação foi enfileirada. Atualize a van e confira o estado dos destinatários.");
      }
      const updated = await apiFetch(`/rotas/${route.id}`);
      setSelectedRoute(updated);
      await loadRoutes(page, searchQuery);
    } catch (e) {
      showError(e.message);
    } finally {
      setRowLoadingId(null);
    }
  };

  const formatLembretesResumo = (lembretes) => {
    if (!lembretes?.total) return "";
    const partes = [];
    if (lembretes.funcionarios_pendentes > 0) {
      partes.push(`${lembretes.funcionarios_pendentes} passageiro(s)`);
    }
    if (lembretes.motorista_inicio_pendente) partes.push("início da rota");
    if (lembretes.motorista_km_final_pendente) partes.push("KM final");
    return partes.join(", ");
  };

  const confirmResendReminders = async (route, groupIndex) => {
    setRowLoadingId(route.id);
    try {
      const result = await resendRouteGroupReminders({
        routeId: route.id,
        groupIndex,
      });
      if (result?.notificacao?.skipped) {
        const motivo = result.notificacao.erro || result.notificacao.motivo || "webhook indisponível";
        showError(`Lembretes não enviados: ${motivo}`);
      } else {
        showToast(`Lembretes enviados (${result?.total_mensagens || 0} mensagem(ns)).`);
      }
      const updated = await apiFetch(`/rotas/${route.id}`);
      setSelectedRoute(updated);
    } catch (e) {
      showError(e.message);
    } finally {
      setRowLoadingId(null);
    }
  };

  const editRoute = async (route) => {
    try {
      const details = await apiFetch(`/rotas/${route.id}`);
      onEditRoute?.(details);
      setSelectedRoute(null);
    } catch (e) {
      showError(e.message);
    }
  };

  const confirmRoute = async (route, { ignorarValidacaoTelefone = false } = {}) => {
    let routeData = route;
    if (!Array.isArray(route?.payload?.grupos) || !route.payload.grupos.length) {
      try {
        routeData = await apiFetch(`/rotas/${route.id}`);
      } catch (e) {
        showError(e.message);
        return { ok: false };
      }
    }
    const groups = Array.isArray(routeData?.payload?.grupos) ? routeData.payload.grupos : [];

    if (!groups.length) {
      showError("Essa rota não possui grupos salvos para confirmação.");
      return { ok: false };
    }

    setRowLoadingId(route.id);
    try {
      const dispatchResults = await dispatchRouteGroups({
        trecho: routeData.trecho,
        horario: routeData.horario,
        data: routeData.data,
        idPedido: routeData.id_pedido,
        groups,
        routeId: routeData.id,
        ignorarValidacaoTelefone,
      });

      await finalizeRouteDispatch({ routeId: routeData.id });

      await loadRoutes(page, searchQuery);
      if (selectedRoute?.id === route.id) {
        const updated = await apiFetch(`/rotas/${route.id}`);
        setSelectedRoute(updated);
      }
      const expiredPreviousRoutes = dispatchResults
        .flatMap((item) => item?.pendencias_anteriores_expiradas || [])
        .filter((item, index, all) => (
          all.findIndex((candidate) => Number(candidate.rota_id) === Number(item.rota_id)) === index
        ));
      showToast(
        expiredPreviousRoutes.length
          ? `Rota enviada. ${expiredPreviousRoutes.length} pendência anterior do motorista expirou sem resposta; confira a timeline anterior.`
          : "Rota confirmada e enviada."
      );
      return { ok: true };
    } catch (e) {
      if (e?.code === "TELEFONES_WHATSAPP_INVALIDOS" && !ignorarValidacaoTelefone) {
        return {
          ok: false,
          needsPhoneOverride: true,
          falhas: Array.isArray(e.falhas) ? e.falhas : [],
        };
      }
      if (e?.code === "ROUTE_COMMUNICATION_PENDING" && routeData.lote_operacional_id) {
        await loadRoutes(page, searchQuery);
        if (selectedRoute?.id === route.id) {
          const updated = await apiFetch(`/rotas/${route.id}`);
          setSelectedRoute(updated);
        }
        showToast(formatRouteDispatchError(e));
        return { ok: true, pending: true };
      }

      let refreshedRoute = null;
      if (e?.code === "ROUTE_COMMUNICATION_FAILED") {
        await loadRoutes(page, searchQuery);
        try {
          refreshedRoute = await apiFetch(`/rotas/${route.id}`);
          if (selectedRoute?.id === route.id) setSelectedRoute(refreshedRoute);
        } catch {
          // A mensagem da espera ainda informa a falha; a atualização manual
          // continua disponível caso a segunda consulta também tenha falhado.
        }
      }

      const dispatchMessage = e?.code === "ROUTE_COMMUNICATION_FAILED"
        ? formatRouteDispatchFailureMessage(refreshedRoute || routeData)
        : formatRouteDispatchError(e);
      const suffix = e?.code === "MOTORISTA_RESPOSTA_PENDENTE"
        ? " Abra a van pendente e aguarde a resposta no WhatsApp."
        : e?.code === "TELEFONES_WHATSAPP_INVALIDOS"
          ? " A van permanece disponível para você corrigir os cadastros."
          : e?.code === "ROUTE_COMMUNICATION_FAILED"
            ? " A van continua salva e o botão de nova tentativa permanece disponível."
            : " Se algum grupo já tiver sido enviado, a próxima tentativa vai retomar sem duplicar o que já saiu.";
      showError(dispatchMessage + suffix);
      return { ok: false };
    } finally {
      setRowLoadingId(null);
    }
  };

  const confirmRouteIgnoringPhoneValidation = async () => {
    if (!phoneValidationDialog?.route) return;
    const route = phoneValidationDialog.route;
    setPhoneValidationDialog(null);
    const result = await confirmRoute(route, { ignorarValidacaoTelefone: true });
    if (result?.needsPhoneOverride) {
      showError("A validação de telefones ainda falhou. Tente novamente ou corrija os cadastros.");
    }
  };

  const deleteRoute = async (route, justificativa) => {
    setRowLoadingId(route.id);
    try {
      const result = await apiFetch(`/rotas/${route.id}`, {
        method: "DELETE",
        body: JSON.stringify({
          justificativa: justificativa.trim(),
          confirm_external_effects: requiresDeleteConfirmationText(route),
        }),
      });
      await loadRoutes(page, searchQuery);
      if (selectedRoute?.id === route.id) {
        closeRoute();
      }
      const preservedExternal = Number(result?.notification_reconciliation?.preserved_external) || 0;
      showToast(
        preservedExternal > 0
          ? `Rota excluída. ${preservedExternal} notificação(ões) já aceita(s) pela Meta foi(ram) preservada(s) na auditoria.`
          : result?.van_individual_excluida
          ? "Van excluída; demais vans e pedidos compartilhados preservados."
          : canCancelProgramPlan(route)
          ? "Planejamento cancelado; programações preservadas."
          : isProgramDeleteAfterResponse(route)
          ? "Rota excluída; pedidos de origem preservados como cancelados."
          : canEditDraft(route)
          ? "Van removida da operação."
          : "Rota excluída do sistema."
      );
      return true;
    } catch (e) {
      showError(e.message);
      return false;
    } finally {
      setRowLoadingId(null);
    }
  };

  return (
    <div className="fade-in">
      {!embedded && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <div>
          <PageTitle icon="route">Acompanhamento das vans</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>
            Confira os envios, as respostas dos motoristas e o andamento de cada van.
          </p>
        </div>
        <Btn variant="secondary" onClick={loadRoutes}><Icon name="refresh" /> Atualizar</Btn>
      </div>
      )}
      {embedded && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ color: "var(--muted)", fontSize: '0.9375rem', lineHeight: 1.5 }}>
            Confira se a van foi enviada e se o motorista respondeu. Mensagem lida no WhatsApp ainda não é uma resposta.
          </div>
          <Btn variant="secondary" size="sm" onClick={() => loadRoutes(page, searchQuery)}><Icon name="refresh" /> Atualizar</Btn>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        {!embedded && (isMobile ? (
          <div style={{ display: "grid", gap: 10 }}>
            <Card style={{ padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 800, letterSpacing: 0.5, color: "var(--muted)" }}>{formatCalendarMonth(visibleCalendarAnchor)}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: 2 }}>{visibleCalendarAnchor.getFullYear()}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Btn size="sm" variant="secondary" onClick={() => setCalendarAnchor(addDays(visibleCalendarAnchor, -5))}>←</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => setCalendarAnchor(addDays(visibleCalendarAnchor, 5))}>→</Btn>
                  <Btn
                    size="sm"
                    variant={selectedDateKey ? "secondary" : "primary"}
                    onClick={() => { setSelectedDateKey(""); setPage(1); }}
                    style={{ justifyContent: "center" }}
                  >
                    Ver todas
                  </Btn>
                </div>
              </div>
            </Card>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${calendarDays.length}, minmax(0, 1fr))`,
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--s2)",
                overflow: "hidden",
              }}
            >
              {calendarDays.map((day, idx) => {
                const dateKey = buildDateKey(day);
                const count = routeCountsByDate[dateKey] || 0;
                const isSelected = selectedDateKey === dateKey;
                const isToday = buildDateKey(new Date()) === dateKey;
                const isLast = idx === calendarDays.length - 1;

                return (
                  <button
                    key={dateKey}
                    onClick={() => { setSelectedDateKey(dateKey); setPage(1); }}
                    style={{
                      minWidth: 0,
                      padding: "12px 4px",
                      border: "none",
                      borderRight: isLast ? "none" : "1px solid var(--border)",
                      background: isSelected ? "#d95b4f" : "transparent",
                      color: isSelected ? "#fff" : "var(--text)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      textAlign: "center",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <div style={{ fontSize: '1.0625rem', fontWeight: 800, lineHeight: 1.1 }}>
                      {day.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                    </div>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 700, opacity: isSelected ? 0.92 : 0.6 }}>
                      {formatShortWeekday(day)}
                    </div>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: isSelected ? "#fff" : (count > 0 ? "#fdba74" : "var(--muted)") }}>
                      {count > 0 ? `${count} rota${count > 1 ? "s" : ""}` : "—"}
                    </div>
                    {isToday && !isSelected && (
                      <span style={{ position: "absolute", inset: 4, border: "1px solid rgba(249,115,22,0.35)", borderRadius: 8, pointerEvents: "none" }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `102px repeat(${calendarDays.length}, minmax(0, 1fr))`,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--s2)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "10px 12px", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8, justifyContent: "center", background: "rgba(255,255,255,0.02)" }}>
              <div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 800, letterSpacing: 0.5, color: "var(--muted)" }}>{formatCalendarMonth(visibleCalendarAnchor)}</div>
                <div style={{ fontSize: '1.0625rem', fontWeight: 700, marginTop: 1 }}>{visibleCalendarAnchor.getFullYear()}</div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <Btn size="sm" variant="secondary" onClick={() => setCalendarAnchor(addDays(visibleCalendarAnchor, -7))}>←</Btn>
                <Btn size="sm" variant="secondary" onClick={() => setCalendarAnchor(addDays(visibleCalendarAnchor, 7))}>→</Btn>
              </div>
              <Btn
                size="sm"
                variant={selectedDateKey ? "secondary" : "primary"}
                onClick={() => { setSelectedDateKey(""); setPage(1); }}
                style={{ justifyContent: "center", fontSize: '0.8125rem', padding: "5px 8px" }}
              >
                Ver todas
              </Btn>
            </div>

            {calendarDays.map((day, idx) => {
              const dateKey = buildDateKey(day);
              const count = routeCountsByDate[dateKey] || 0;
              const isSelected = selectedDateKey === dateKey;
              const isToday = buildDateKey(new Date()) === dateKey;
              const isLast = idx === calendarDays.length - 1;

              return (
                <button
                  key={dateKey}
                  onClick={() => { setSelectedDateKey(dateKey); setPage(1); }}
                  style={{
                    padding: "10px 6px",
                    border: "none",
                    borderRight: isLast ? "none" : "1px solid var(--border)",
                    background: isSelected ? "#d95b4f" : "transparent",
                    color: isSelected ? "#fff" : "var(--text)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    transition: "background 0.15s",
                    minWidth: 0,
                  }}
                >
                  <div style={{ fontSize: '1rem', fontWeight: 800, letterSpacing: -0.10 }}>
                    {day.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, opacity: isSelected ? 0.9 : 0.55, letterSpacing: 0.4 }}>
                    {formatShortWeekday(day)}
                  </div>
                  {count > 0 ? (
                    <span style={{
                      marginTop: 2,
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      padding: "2px 7px",
                      borderRadius: 999,
                      background: isSelected ? "rgba(255,255,255,0.2)" : "rgba(249,115,22,0.15)",
                      color: isSelected ? "#fff" : "#fdba74",
                      whiteSpace: "nowrap",
                    }}>
                      {count} rota{count > 1 ? "s" : ""}
                    </span>
                  ) : (
                    <span style={{ marginTop: 2, fontSize: '0.8125rem', color: isSelected ? "rgba(255,255,255,0.45)" : "var(--muted)", fontWeight: 500 }}>
                      —
                    </span>
                  )}
                  {isToday && !isSelected && (
                    <span style={{ position: "absolute", inset: 4, border: "1px solid rgba(249,115,22,0.35)", borderRadius: 8, pointerEvents: "none" }} />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {(isSearchControlled || !embedded) && (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          {!isSearchControlled && (
          <Input
            placeholder="Buscar por código do pedido, motorista ou região..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          )}
          <Select
            aria-label="Filtrar por status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: 180 }}
          >
            <option value="">Todos os status</option>
            <option value="rascunho">Aguardando envio</option>
            <option value="enviada">Enviado ao motorista</option>
          </Select>
        </div>
      </Card>
      )}

      {loading && <Card style={{ textAlign: "center", padding: 40 }}><Spinner /></Card>}

      {!loading && totalRoutes === 0 && (
        <Card style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
          <div style={{ color: "var(--text)", fontWeight: 700, marginBottom: 8 }}>
            {effectiveDateKey ? `Nenhuma van para ${formatDate(effectiveDateKey)}.` : "Nenhuma van encontrada."}
          </div>
          <div>As vans aparecem aqui quando são preparadas para envio. Confira também a aba Planejamento.</div>
          {effectiveDateKey && <Btn size="sm" variant="secondary" style={{ marginTop: 16 }} onClick={() => { setEffectiveDateKey(""); setPage(1); }}>Ver todas as datas</Btn>}
        </Card>
      )}

      {!loading && totalRoutes > 0 && (
        <>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 12px", width: 42 }} />
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Pedido</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Data</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Trecho</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Status</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Resposta do motorista</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Resumo</th>
                    <th style={{ padding: "12px 16px", textAlign: "right", fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((route, index) => {
                    const status = getBacklogStatusMeta(route);
                    const motoristaResponse = getMotoristaResponseMeta(route);
                    const isBusy = rowLoadingId === route.id;
                    const routeRegions = Array.isArray(route.regioes) ? route.regioes : [];
                    const manualRoute = isManualRoute(route);
                    const pedidoCodes = getRoutePedidoCodes(route);

                    return (
                      <tr key={route.id} style={{ borderBottom: index < routes.length - 1 ? "1px solid var(--border)" : "none" }}>
                        <td style={{ padding: "14px 12px", verticalAlign: "top" }}>
                          {manualRoute ? (
                            <span title="Rota criada manualmente, sem pedido Embraer vinculado">🛠</span>
                          ) : route.lote_operacional_id ? (
                            <span title="Grupo de pedidos planejados"><Icon name="orders" /></span>
                          ) : null}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div style={{ fontWeight: 600 }}>
                              {pedidoCodes.length ? pedidoCodes.join(", ") : (route.id_pedido || `Van #${route.id}`)}
                            </div>
                            {manualRoute && (
                              <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 999, background: "rgba(113,113,122,0.18)", color: "#d4d4d8", fontSize: '0.8125rem', fontWeight: 700 }}>
                                rota manual
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4 }}>{route.horario || "Sem horário"}</div>
                        </td>
                        <td style={{ padding: "14px 16px", color: "var(--muted)" }}>{formatDate(route.data)}</td>
                        <td style={{ padding: "14px 16px" }}>{formatTrecho(route.trecho, config, route.destino_nome)}</td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 999, background: status.bg, color: status.color, fontSize: '0.875rem', fontWeight: 700 }}>
                              {status.label}
                            </span>
                            {route.is_teste && (
                              <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 999, background: "rgba(249,115,22,0.12)", color: "#fdba74", fontSize: '0.875rem', fontWeight: 700 }}>
                                Teste
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px", verticalAlign: "top" }}>
                          <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 999, background: motoristaResponse.bg, color: motoristaResponse.color, fontSize: '0.875rem', fontWeight: 700, whiteSpace: "nowrap" }}>
                            {motoristaResponse.label}
                          </span>
                          {route.ultima_resposta_motorista_em && (
                            <div style={{ marginTop: 5, color: "var(--muted)", fontSize: '0.8125rem' }}>
                              Última: {formatDateTime(route.ultima_resposta_motorista_em)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>
                          {route.total_passageiros || 0} passageiro(s)
                          {Array.isArray(route.grupos_resumo) && route.grupos_resumo.length > 0 && (
                            <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                              {route.grupos_resumo.map((grupo) => (
                                <div key={`${route.id}-grupo-${grupo.grupo_index}`} style={{ fontSize: '0.875rem', lineHeight: 1.45 }}>
                                  <strong>Van {grupo.planejamento_van_numero || grupo.grupo_index + 1}</strong>
                                  {grupo.pedido_responsavel ? ` · pedido ${grupo.pedido_responsavel}` : ""}
                                  {grupo.motorista_executor ? ` · ${grupo.motorista_executor}` : ""}
                                  {grupo.total_passageiros != null && grupo.capacidade
                                    ? ` · ${grupo.total_passageiros}/${grupo.capacidade} lugares`
                                    : ""}
                                  {grupo.passageiros_transferidos > 0 && (
                                    <span style={{ color: "#fde68a" }}>
                                      {" "}· {grupo.passageiros_transferidos} transferido(s)
                                    </span>
                                  )}
                                  {grupo.passageiros_por_pedido && Object.keys(grupo.passageiros_por_pedido).length > 0 && (
                                    <div style={{ color: "var(--muted)", marginTop: 2 }}>
                                      {Object.entries(grupo.passageiros_por_pedido)
                                        .map(([codigo, qtd]) => `${codigo}: ${qtd}`)
                                        .join(" · ")}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {route.total_passageiros_transferidos > 0 && (
                            <div style={{ marginTop: 4, fontSize: '0.875rem', color: "#fde68a" }}>
                              {route.total_passageiros_transferidos} passageiro(s) em van de outro pedido
                            </div>
                          )}
                          {route.ultima_mensagem_status && (
                            <div style={{ marginTop: 4 }}>{route.ultima_mensagem_status}</div>
                          )}
                          {route.ultimo_evento_em && (
                            <div style={{ marginTop: 4, fontSize: '0.875rem' }}>
                              Última atualização: {formatDateTime(route.ultimo_evento_em)}
                            </div>
                          )}
                          {hasRouteDispatchFailure(route) && (
                            <div style={{ marginTop: 6, fontSize: '0.875rem', color: "#fca5a5", lineHeight: 1.45 }}>
                              {formatRouteDispatchFailureMessage(route)}
                            </div>
                          )}
                          {route.tem_envio_iniciado && !hasRouteDispatchFailure(route) && !route.tem_resposta_motorista && (route.status === "rascunho" || route.status === "enviada") && (
                            <div style={{ marginTop: 4, fontSize: '0.875rem', color: "#fde68a" }}>
                              Disparo enviado, aguardando resposta do motorista. Você ainda pode editar ou excluir.
                            </div>
                          )}
                          {route.tem_resposta_motorista && (route.status === "rascunho" || route.status === "enviada") && (
                            <div style={{ marginTop: 4, fontSize: '0.875rem', color: "#fde68a" }}>
                              Motorista já respondeu. Horário e PG ainda podem ser corrigidos nos detalhes da van, sem novo envio pela Meta.
                            </div>
                          )}
                          {routeRegions.length > 0 && (
                            <div style={{ marginTop: 4 }}>{routeRegions.join(", ")}</div>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                            <Btn size="sm" variant="secondary" onClick={() => openRoute(route)} disabled={isBusy}>
                              Visualizar
                            </Btn>
                            {canEditDraft(route) && (
                              <Btn
                                size="sm"
                                variant="secondary"
                                onClick={() => editRoute(route)}
                                disabled={isBusy}
                              >
                                Editar
                              </Btn>
                            )}
                            {canStartRouteDispatch(route) && (
                              <Btn size="sm" onClick={() => openConfirmRouteDialog(route)} disabled={isBusy}>
                                {isBusy ? <><Spinner /> Confirmando</> : "Revisar e enviar"}
                              </Btn>
                            )}
                            {canRetryRouteDispatch(route) && (
                              <Btn
                                size="sm"
                                variant="danger"
                                onClick={() => openConfirmRouteDialog({ ...route, retry_dispatch: true })}
                                disabled={isBusy}
                              >
                                {isBusy ? <><Spinner /> Tentando</> : "Tentar envio novamente"}
                              </Btn>
                            )}
                            {canDeleteRoute(route) && (
                              <Btn size="sm" variant="danger" onClick={() => setConfirmDialog({ type: "deleteRoute", route, confirmationText: "" })} disabled={isBusy}>
                                Excluir
                              </Btn>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={totalRoutes} perPage={PER_PAGE} onChange={setPage} />
          </Card>
        </>
      )}

      {selectedRoute && (
        <Modal title={`Van ${getRoutePedidoCodes(selectedRoute).join(", ") || selectedRoute.id_pedido || `#${selectedRoute.id}`}`} onClose={closeRoute}>
          {isManualRoute(selectedRoute) && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 999, background: "rgba(113,113,122,0.18)", color: "#d4d4d8", fontSize: '0.875rem', fontWeight: 700 }}>
                rota manual — criada sem pedido Embraer vinculado
              </span>
            </div>
          )}
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ padding: "12px 14px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, fontSize: '0.9375rem', lineHeight: 1.5 }}>
              {selectedRoute.status_operacional === "finalizada"
                ? "Esta van foi finalizada. Consulte os documentos e as confirmações abaixo."
                : selectedRoute.status_operacional === "motorista_nao_respondeu"
                  ? "O prazo de resposta do motorista expirou. Confira o histórico e os dados antes de decidir o próximo envio."
                : canRetryRouteDispatch(selectedRoute)
                ? "O envio falhou. Confira os dados e use “Tentar envio novamente” para esta van."
                : canStartRouteDispatch(selectedRoute)
                  ? "Esta van ainda não foi enviada. Confira motorista, veículo e passageiros antes de enviar."
                  : selectedRoute.tem_resposta_motorista
                    ? "O motorista já respondeu. Acompanhe o mapa, as confirmações dos passageiros e a finalização abaixo."
                    : "Aguarde a resposta do motorista no WhatsApp. A leitura da mensagem não confirma o início da viagem."}
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ background: "var(--s2)", borderRadius: 10, padding: "12px 14px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: '0.8125rem', color: "var(--muted)", textTransform: "uppercase", fontWeight: 700 }}>Data e horário</div>
                <div style={{ marginTop: 6, fontWeight: 700 }}>{formatDate(selectedRoute.data)} às {selectedRoute.horario || "—"}</div>
              </div>
              <div style={{ background: "var(--s2)", borderRadius: 10, padding: "12px 14px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: '0.8125rem', color: "var(--muted)", textTransform: "uppercase", fontWeight: 700 }}>Trecho</div>
                <div style={{ marginTop: 6, fontWeight: 700 }}>{formatTrecho(selectedRoute.trecho, config, selectedRoute.destino_nome)}</div>
              </div>
              <div style={{ background: "var(--s2)", borderRadius: 10, padding: "12px 14px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: '0.8125rem', color: "var(--muted)", textTransform: "uppercase", fontWeight: 700 }}>Status da automação</div>
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <OperationalBadge status={selectedRoute.status_operacional} style={{ padding: "4px 10px" }} />
                  {selectedRoute.is_teste && (
                    <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 999, background: "rgba(249,115,22,0.12)", color: "#fdba74", fontSize: '0.875rem', fontWeight: 700 }}>
                      Teste
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 8, color: "var(--muted)", fontSize: '0.9375rem' }}>
                  {selectedRoute.ultima_mensagem_status || "Nenhuma atualização recebida."}
                </div>
                {Array.isArray(selectedRoute.respostas_motorista) && selectedRoute.respostas_motorista.length > 0 && (
                  <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
                    <div style={{ fontSize: '0.8125rem', color: "var(--muted)", textTransform: "uppercase", fontWeight: 700 }}>
                      Resposta origem/destino
                    </div>
                    {selectedRoute.respostas_motorista.map((item) => (
                      <div
                        key={item.id || `${item.motorista_telefone}-${item.respondido_em}`}
                        style={{ fontSize: '0.9375rem', color: "var(--text)", lineHeight: 1.45 }}
                      >
                        <Icon name="route" /> <strong>{formatMotoristaRespostaLabel(item.resposta, config)}</strong>
                        {" — "}
                        {formatDateTime(item.respondido_em)}
                        {item.motorista_telefone ? (
                          <span style={{ color: "var(--muted)" }}> · {item.motorista_telefone}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
                {duplicateMotoristaRespostas > 0 && (
                  <div style={{ marginTop: 10, fontSize: '0.875rem', color: "#fbbf24", lineHeight: 1.45 }}>
                    <Icon name="warning" /> Detectadas {duplicateMotoristaRespostas} resposta(s) duplicada(s) no histórico antes do bloqueio de duplicidades.
                  </div>
                )}
                {selectedRouteMapLinks.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                    {selectedRouteMapLinks.map((map, index) => (
                      <a
                        key={`${map.grupo_index ?? "sem-grupo"}-${map.link_mapa}`}
                        href={map.link_mapa}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "fit-content",
                          padding: "8px 12px",
                          borderRadius: 10,
                          border: "1px solid rgba(249,115,22,0.35)",
                          background: "rgba(249,115,22,0.10)",
                          color: "#fdba74",
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          textDecoration: "none",
                        }}
                      >
                        {selectedRouteMapLinks.length > 1
                          ? `Abrir mapa da van ${(map.grupo_index ?? index) + 1}`
                          : "Abrir rota no mapa"}
                      </a>
                    ))}
                  </div>
                )}
                {selectedRoute.tem_resposta_motorista && selectedRouteMapLinks.length === 0 && (
                  <div style={{ marginTop: 10, fontSize: '0.875rem', color: "#fbbf24", lineHeight: 1.45 }}>
                    <Icon name="warning" /> O motorista respondeu, mas ainda não há mapa registrado para esta rota.
                    Verifique a timeline para identificar geração em andamento ou falha.
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              {(selectedRoute.payload?.grupos || []).map((group, index) => {
                const envioGrupo = getEnvioGrupo(selectedRoute, index);
                const podeAdicionarPassageiro = Boolean(envioGrupo?.pode_adicionar_passageiro);
                const podeRemoverPassageiro = Boolean(envioGrupo?.pode_remover_passageiro)
                  && (group.pessoas || []).length > 1;
                const podeCorrigirDados = Boolean(envioGrupo?.pode_corrigir_dados);
                const podeReenviarNotificacoes = Boolean(envioGrupo?.pode_reenviar_notificacoes);
                const podeReenviarLembretes = Boolean(envioGrupo?.pode_reenviar_lembretes);
                const respostaMotoristaExpirada = Boolean(envioGrupo?.resposta_motorista_expirada_em);
                const lembretesResumo = formatLembretesResumo(envioGrupo?.lembretes);
                const lembreteCooldown = Number(envioGrupo?.lembrete_cooldown_minutos || 0);
                const bloqueioMotivo = envioGrupo?.motorista_iniciou
                  ? "Motorista já iniciou a rota."
                  : respostaMotoristaExpirada
                    ? "O motorista não respondeu dentro do prazo desta PG."
                  : !envioGrupo?.motorista_respondeu
                    ? "Aguardando resposta de origem/destino do motorista."
                    : envioGrupo?.status !== "enviado"
                      ? "Esta van ainda não foi enviada."
                      : "";

                const grupoJaDisparado = [
                  "enviado",
                  "disparando_webhook",
                  "enfileirando_meta",
                  "enfileirado_meta",
                ].includes(envioGrupo?.status);
                const envioStatusLabel = {
                  enviado: "Enviado",
                  disparando_webhook: "Processando automação",
                  enfileirando_meta: "Preparando envio",
                  enfileirado_meta: "Envio em andamento",
                  falha: "Falha no envio",
                }[envioGrupo?.status] || envioGrupo?.status || "Pendente";
                const envioStatusOk = envioGrupo?.status === "enviado";
                const envioStatusFailed = envioGrupo?.status === "falha";
                const podeAlterarExecutor = canEditDraft(selectedRoute)
                  && !envioGrupo?.motorista_iniciou
                  && !grupoJaDisparado;
                const executorNome = group.motorista_executor?.nome || group.motorista?.nome || "—";
                const executorVeiculo = group.veiculo_executor?.veiculo || group.veiculo?.veiculo || "—";
                const executorPlaca = group.veiculo_executor?.placa || group.veiculo?.placa || "";

                return (
                  <Card key={`${group.regiao}-${index}`} style={{ padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{group.planejamento_van_numero ? `Van ${group.planejamento_van_numero} · ${group.regiao}` : group.regiao || `Van ${index + 1}`}</div>
                        <div style={{ fontSize: '0.9375rem', color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>
                          Motorista: <strong style={{ color: "var(--text)" }}>{executorNome}</strong>
                          {" · "}
                          Veículo: <strong style={{ color: "var(--text)" }}>{executorVeiculo}{executorPlaca ? ` (${executorPlaca})` : ""}</strong>
                        </div>
                        {group.programacao_codigo_responsavel && (
                          <div style={{ fontSize: '0.875rem', color: "#93c5fd", marginTop: 6 }}>
                            Pedido responsável: {group.programacao_codigo_responsavel}
                          </div>
                        )}
                        <div style={{ fontSize: '0.875rem', color: "#93c5fd", marginTop: 6 }}>
                          Horário desta van: {formatDate(group.data || selectedRoute.data)}
                          {" · "}{group.horario || selectedRoute.horario || "—"}
                        </div>
                        {envioGrupo && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                            <span style={{
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                              padding: "3px 8px",
                              borderRadius: 999,
                              background: envioStatusOk
                                ? "rgba(34,197,94,0.12)"
                                : envioStatusFailed
                                  ? "rgba(239,68,68,0.12)"
                                  : "rgba(113,113,122,0.15)",
                              color: envioStatusOk ? "#86efac" : envioStatusFailed ? "#fca5a5" : "#a1a1aa",
                            }}>
                              {envioStatusLabel}
                            </span>
                            <span style={{
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                              padding: "3px 8px",
                              borderRadius: 999,
                              background: envioGrupo.motorista_respondeu
                                ? "rgba(34,197,94,0.12)"
                                : respostaMotoristaExpirada
                                  ? "rgba(239,68,68,0.12)"
                                  : "rgba(234,179,8,0.12)",
                              color: envioGrupo.motorista_respondeu
                                ? "#86efac"
                                : respostaMotoristaExpirada
                                  ? "#fca5a5"
                                  : "#fde68a",
                            }}>
                              {envioGrupo.motorista_respondeu
                                ? "Motorista respondeu"
                                : respostaMotoristaExpirada
                                  ? "Não respondeu no prazo"
                                  : "Aguardando motorista"}
                            </span>
                            {envioGrupo.motorista_iniciou && (
                              <span style={{ fontSize: '0.8125rem', fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: "rgba(59,130,246,0.12)", color: "#93c5fd" }}>
                                Rota iniciada
                              </span>
                            )}
                            {envioGrupo.tem_planejamento && (
                              <span style={{ fontSize: '0.8125rem', fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: "rgba(249,115,22,0.12)", color: "#fdba74" }}>
                                Rota planejada
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {podeAlterarExecutor && (
                          <Btn
                            variant="secondary"
                            onClick={() => openExecutorDialog(selectedRoute, group, index)}
                            disabled={rowLoadingId === selectedRoute.id || savingExecutor}
                          >
                            Trocar motorista ou veículo
                          </Btn>
                        )}
                        {envioGrupo?.documento_id && (
                          <Btn
                            variant="secondary"
                            onClick={() => openOperationalDocument(envioGrupo.documento_id)}
                          >
                            {docOperacionalLabel}
                          </Btn>
                        )}
                        {podeCorrigirDados && (
                          <Btn
                            variant="secondary"
                            onClick={() => openCorrectionDialog(selectedRoute, group, index)}
                            disabled={rowLoadingId === selectedRoute.id || savingCorrection}
                          >
                            Corrigir horário/PG
                          </Btn>
                        )}
                        {podeAdicionarPassageiro && (
                          <Btn
                            variant="secondary"
                            onClick={() => openAddPassengerDialog(selectedRoute, group, index)}
                            disabled={rowLoadingId === selectedRoute.id}
                          >
                            {selectedRoute.lote_operacional_id
                              ? "+ Adicionar PG/passageiro"
                              : "+ Adicionar passageiro"}
                          </Btn>
                        )}
                        {podeReenviarNotificacoes && (
                          <Btn
                            variant="secondary"
                            onClick={() => confirmResendNotifications(selectedRoute, index)}
                            disabled={rowLoadingId === selectedRoute.id}
                          >
                            Reenviar notificações
                          </Btn>
                        )}
                        {envioStatusFailed && canRetryRouteDispatch(selectedRoute) && (
                          <Btn
                            variant="danger"
                            onClick={() => setConfirmDialog({ type: "confirmRoute", route: selectedRoute, retry: true })}
                            disabled={rowLoadingId === selectedRoute.id}
                          >
                            Tentar envio novamente
                          </Btn>
                        )}
                        {podeReenviarLembretes && (
                          <Btn
                            variant="secondary"
                            onClick={() => confirmResendReminders(selectedRoute, index)}
                            disabled={rowLoadingId === selectedRoute.id}
                          >
                            Enviar lembretes
                          </Btn>
                        )}
                      </div>
                    </div>
                    {lembretesResumo && (
                      <div style={{ marginTop: 8, fontSize: '0.875rem', color: "#fbbf24", lineHeight: 1.45 }}>
                        Pendências: {lembretesResumo}
                        {lembreteCooldown > 0 && (
                          <span style={{ color: "var(--muted)" }}>
                            {" "}· próximo lembrete em ~{lembreteCooldown} min
                          </span>
                        )}
                      </div>
                    )}
                    {envioStatusFailed && (
                      <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.28)", color: "#fecaca", fontSize: '0.875rem', lineHeight: 1.5 }}>
                        {formatRouteDispatchFailureMessage(envioGrupo)}
                      </div>
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                      {(group.pessoas || []).map((person) => (
                        <span
                          key={person.id || `${person.nome}-${person.telefone}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 7,
                            background: "var(--s3)",
                            borderRadius: 999,
                            padding: podeRemoverPassageiro ? "4px 5px 4px 10px" : "5px 10px",
                            fontSize: '0.875rem',
                            color: "var(--muted)",
                          }}
                        >
                          <span>{person.nome}</span>
                          {podeRemoverPassageiro && (
                            <button
                              type="button"
                              aria-label={`Remover ${person.nome} da rota`}
                              title={`Remover ${person.nome} da rota`}
                              onClick={() => setRemovePassengerDialog({
                                route: selectedRoute,
                                group,
                                groupIndex: index,
                                person,
                              })}
                              disabled={rowLoadingId === selectedRoute.id}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 999,
                                border: "1px solid rgba(239,68,68,0.35)",
                                background: "rgba(239,68,68,0.10)",
                                color: "#fca5a5",
                                cursor: "pointer",
                                lineHeight: 1,
                                fontWeight: 900,
                              }}
                            >
                              ×
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                    {!podeAdicionarPassageiro && selectedRoute.tem_envio_iniciado && bloqueioMotivo && (
                      <div style={{ marginTop: 10, fontSize: '0.875rem', color: "var(--muted)" }}>
                        Alteração de passageiros bloqueada: {bloqueioMotivo}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: '0.875rem', color: "var(--muted)", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>
                  Histórico de envios
                </div>
                <Btn
                  size="sm"
                  variant="secondary"
                  onClick={refreshSelectedRoute}
                  disabled={refreshingSelectedRoute}
                >
                  {refreshingSelectedRoute ? <><Spinner /> Atualizando</> : <><Icon name="refresh" size={16} /> Atualizar histórico</>}
                </Btn>
              </div>
              {(Array.isArray(selectedRoute.eventos) && selectedRoute.eventos.length > 0)
                || persistedMapsWithoutTimelineEvent.length > 0 ? (
                <Card style={{ padding: 16 }}>
                  <div style={{ display: "grid", gap: 12 }}>
                    {persistedMapsWithoutTimelineEvent.map((map, index) => (
                      <div
                        key={`mapa-persistido-${map.documento_id || index}-${map.grupo_index ?? "sem-grupo"}`}
                        style={{ display: "grid", gap: 6, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ display: "inline-flex", padding: "4px 9px", borderRadius: 999, background: "rgba(249,115,22,0.14)", color: "#fdba74", fontSize: '0.8125rem', fontWeight: 700 }}>
                            Mapa persistido
                          </span>
                          <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                            {formatDateTime(map.atualizado_em)}
                          </span>
                          <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>sistema</span>
                        </div>
                        <div style={{ fontWeight: 600 }}>
                          Mapa atual da van {Number(map.grupo_index) + 1} recuperado do planejamento salvo.
                        </div>
                        <div style={{ fontSize: '0.875rem', color: "#fbbf24" }}>
                          O mapa existe, mas o fluxo anterior não gravou o evento correspondente na timeline.
                        </div>
                        {renderEventDetails({
                          link_mapa: map.link_mapa,
                          grupo_index: map.grupo_index,
                          total_links: map.total_links,
                          duracao_minutos: map.duracao_minutos,
                        })}
                      </div>
                    ))}
                    {(selectedRoute.eventos || []).map((event, eventIndex) => {
                      const isDuplicateMotoristaResposta = event?.tipo === "motorista_respondeu" && selectedRoute.eventos
                        .slice(0, eventIndex)
                        .some((prev) => (
                          prev?.tipo === "motorista_respondeu"
                          && String(prev?.detalhes?.motorista_telefone || "").replace(/\D/g, "")
                            === String(event?.detalhes?.motorista_telefone || "").replace(/\D/g, "")
                        ));
                      return (
                        <div key={event.id} style={{ display: "grid", gap: 6, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <OperationalBadge status={event.status} />
                            <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {formatDateTime(event.criado_em)}
                            </span>
                            <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {event.origem || "sistema"}
                            </span>
                          </div>
                          <div style={{ fontWeight: 600 }}>{event.mensagem || event.tipo}</div>
                          {event.tipo && (
                            <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                              {EVENT_TYPE_LABELS[event.tipo] || `Evento: ${event.tipo}`}
                              {event.tipo === "motorista_respondeu" && event.detalhes?.resposta && (
                                <span>
                                  {" "}· Resposta: {formatMotoristaRespostaLabel(event.detalhes.resposta === "1" ? "1" : event.detalhes.resposta === "2" ? "2" : event.detalhes.resposta_normalizada, config)}
                                </span>
                              )}
                            </div>
                          )}
                          {isDuplicateMotoristaResposta && (
                            <div style={{ fontSize: '0.875rem', color: "#fbbf24" }}>
                              Resposta duplicada (ignorada pela API a partir do bloqueio).
                            </div>
                          )}
                          {renderEventDetails(event.detalhes)}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ) : (
                <Card style={{ padding: 16, color: "var(--muted)" }}>
                  Nenhum evento registrado para esta rota.
                </Card>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
              <Btn variant="secondary" onClick={closeRoute}>Fechar</Btn>
              {canEditDraft(selectedRoute) && (
                <Btn
                  variant="secondary"
                  onClick={() => editRoute(selectedRoute)}
                  disabled={rowLoadingId === selectedRoute.id}
                >
                  Editar rota
                </Btn>
              )}
              {canStartRouteDispatch(selectedRoute) && (
                <Btn
                  onClick={() => setConfirmDialog({ type: "confirmRoute", route: selectedRoute })}
                  disabled={rowLoadingId === selectedRoute.id}
                >
                  {rowLoadingId === selectedRoute.id ? <><Spinner /> Confirmando</> : "Revisar e enviar"}
                </Btn>
              )}
              {canRetryRouteDispatch(selectedRoute) && (
                <Btn
                  variant="danger"
                  onClick={() => setConfirmDialog({ type: "confirmRoute", route: selectedRoute, retry: true })}
                  disabled={rowLoadingId === selectedRoute.id}
                >
                  {rowLoadingId === selectedRoute.id ? <><Spinner /> Tentando</> : "Tentar envio novamente"}
                </Btn>
              )}
              {onOpenPlanning && selectedRoute.planejamento_origem_id && <Btn variant="secondary" onClick={() => onOpenPlanning(selectedRoute.planejamento_origem_id)}>Ver outras vans do planejamento</Btn>}
              {canDeleteRoute(selectedRoute) && (
                <Btn
                  variant="danger"
                  onClick={() => setConfirmDialog({ type: "deleteRoute", route: selectedRoute, confirmationText: "" })}
                  disabled={rowLoadingId === selectedRoute.id}
                >
                  Excluir
                </Btn>
              )}
            </div>
          </div>
        </Modal>
      )}

      {correctionDialog && (
        <Modal
          title={`Corrigir dados — ${correctionDialog.group?.regiao || `Van ${correctionDialog.groupIndex + 1}`}`}
          onClose={() => {
            if (!savingCorrection) setCorrectionDialog(null);
          }}
        >
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ padding: 12, borderRadius: 10, border: "1px solid rgba(249,115,22,0.35)", background: "rgba(249,115,22,0.08)", color: "#fdba74", fontSize: '0.9375rem', lineHeight: 1.5 }}>
              Esta correção altera somente os dados internos do RotaFlex. A resposta do motorista, documentos, assinaturas e mensagens já enviadas serão preservados; nenhuma mensagem nova será enviada pela Meta.
            </div>

            <Input
              label="Horário da van *"
              type="time"
              value={correctionDialog.horario}
              onChange={(event) => setCorrectionDialog((current) => ({
                ...current,
                horario: event.target.value,
                confirmed: false,
              }))}
            />

            {correctionDialog.route?.lote_operacional_id && correctionDialog.programacoes.length > 0 && (
              <Select
                label="PG a corrigir *"
                value={correctionDialog.programacaoId}
                onChange={(event) => {
                  const programacaoId = event.target.value;
                  const selected = correctionDialog.programacoes.find(
                    (item) => Number(item.id) === Number(programacaoId)
                  );
                  setCorrectionDialog((current) => ({
                    ...current,
                    programacaoId,
                    programacaoCodigo: selected?.codigo || "",
                    confirmed: false,
                  }));
                }}
              >
                {correctionDialog.programacoes.map((programacao) => (
                  <option key={programacao.id} value={programacao.id}>
                    PG {programacao.codigo}
                  </option>
                ))}
              </Select>
            )}

            <Input
              label="Número correto da PG *"
              placeholder="Ex.: 2026/58885"
              value={correctionDialog.programacaoCodigo}
              onChange={(event) => setCorrectionDialog((current) => ({
                ...current,
                programacaoCodigo: event.target.value,
                confirmed: false,
              }))}
            />

            <label style={{ display: "grid", gap: 6, fontSize: '0.9375rem', fontWeight: 700 }}>
              Motivo da correção *
              <textarea
                rows={3}
                maxLength={500}
                value={correctionDialog.motivo}
                onChange={(event) => setCorrectionDialog((current) => ({
                  ...current,
                  motivo: event.target.value,
                  confirmed: false,
                }))}
                placeholder="Ex.: horário digitado incorretamente no cadastro"
                style={{ width: "100%", resize: "vertical", borderRadius: 8, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", padding: "10px 12px", font: "inherit" }}
              />
            </label>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: '0.9375rem', lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={correctionDialog.confirmed}
                onChange={(event) => setCorrectionDialog((current) => ({
                  ...current,
                  confirmed: event.target.checked,
                }))}
                style={{ marginTop: 3 }}
              />
              <span>Confirmo os novos dados e estou ciente de que esta alteração não modifica nem reenvia mensagens no WhatsApp.</span>
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn variant="secondary" onClick={() => setCorrectionDialog(null)} disabled={savingCorrection}>
                Cancelar
              </Btn>
              <Btn onClick={saveRouteCorrection} disabled={savingCorrection || !correctionDialog.confirmed}>
                {savingCorrection ? <><Spinner /> Salvando</> : "Salvar correção"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {addPassengerDialog && (() => {
        const grupoAtual = selectedRoute?.payload?.grupos?.[addPassengerDialog.groupIndex]
          || addPassengerDialog.group;
        const pessoasNaRota = grupoAtual?.pessoas || [];
        const funcionariosDisponiveis = funcionarios.filter((funcionario) => {
          if (Number(funcionario.cliente_id) !== Number(addPassengerDialog.route?.cliente_id)) {
            return false;
          }
          const jaNaRota = pessoasNaRota.some(
            (pessoa) => Number(pessoa.id) === Number(funcionario.id)
          );
          if (jaNaRota) return false;
          const term = passengerSearch.trim().toLowerCase();
          if (!term) return true;
          return [funcionario.nome, funcionario.bairro, funcionario.rua, funcionario.cidade]
            .some((value) => String(value || "").toLowerCase().includes(term));
        });
        const selecionados = selectedFuncionarioIds.size;
        const routeProgramacoes = collectRouteProgramacoes(addPassengerDialog.route);
        const routeProgramacaoIds = new Set(routeProgramacoes.map((item) => Number(item.id)));
        const programacaoMap = new Map(routeProgramacoes.map((item) => [Number(item.id), {
          ...item,
          vinculada: true,
        }]));
        for (const programacao of programacoesDisponiveis) {
          if (!programacaoMap.has(Number(programacao.id))) {
            programacaoMap.set(Number(programacao.id), { ...programacao, vinculada: false });
          }
        }
        const programacaoTerm = programacaoSearch.trim().toLowerCase();
        const programacoesFiltradas = [...programacaoMap.values()].filter((programacao) => (
          !programacaoTerm
          || String(programacao.codigo || "").toLowerCase().includes(programacaoTerm)
        ));
        const requiresProgramacao = Boolean(addPassengerDialog.route?.lote_operacional_id);
        const isNewProgramacao = programacaoMode === PROGRAMACAO_MODE_CREATE;
        const isExistingProgramacaoUnlinked = !isNewProgramacao
          && Boolean(selectedProgramacaoId)
          && !routeProgramacaoIds.has(Number(selectedProgramacaoId));
        const aguardandoMotorista = !getEnvioGrupo(
          selectedRoute || addPassengerDialog.route,
          addPassengerDialog.groupIndex
        )?.motorista_respondeu;

        return (
        <Modal
          title={`Adicionar passageiros — ${grupoAtual?.regiao || `Grupo ${addPassengerDialog.groupIndex + 1}`}`}
          onClose={() => setAddPassengerDialog(null)}
        >
          <div style={{ display: "grid", gap: 14 }}>
            <p style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.5, margin: 0 }}>
              {aguardandoMotorista
                ? "Selecione um ou mais funcionários. Nenhuma mensagem será enviada agora: quando o motorista responder, o primeiro mapa e as confirmações já considerarão os novos endereços."
                : "Selecione um ou mais funcionários. Todos receberão o link de confirmação e o motorista receberá a rota recalculada uma única vez."}
              {" "}
              Só é possível incluir passageiros antes do motorista iniciar a rota.
            </p>

            {requiresProgramacao && (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: '0.9375rem', fontWeight: 700 }}>PG de origem *</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    [PROGRAMACAO_MODE_EXISTING, "Buscar PG existente"],
                    [PROGRAMACAO_MODE_CREATE, "Criar nova PG"],
                  ].map(([mode, label]) => {
                    const active = programacaoMode === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          setProgramacaoMode(mode);
                          setConfirmNewProgramacaoLink(false);
                        }}
                        style={{
                          padding: "10px 12px",
                          borderRadius: 9,
                          border: `1px solid ${active ? "rgba(249,115,22,0.7)" : "var(--border)"}`,
                          background: active ? "rgba(249,115,22,0.14)" : "var(--s2)",
                          color: active ? "#fb923c" : "var(--text)",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {requiresProgramacao && !isNewProgramacao && (
              <div style={{ display: "grid", gap: 10 }}>
                <Input
                  label="Buscar pelo número da PG"
                  placeholder="Ex.: 2026/58885"
                  value={programacaoSearch}
                  onChange={(event) => setProgramacaoSearch(event.target.value)}
                />
                <Select
                  label="PG encontrada *"
                  value={selectedProgramacaoId}
                  onChange={(event) => {
                    setSelectedProgramacaoId(event.target.value);
                    setConfirmNewProgramacaoLink(false);
                  }}
                >
                  <option value="">Selecione uma PG</option>
                  {programacoesFiltradas.map((programacao) => (
                    <option key={programacao.id} value={programacao.id}>
                      PG {programacao.codigo}{programacao.vinculada ? " · já vinculada à rota" : " · disponível"}
                    </option>
                  ))}
                </Select>
                {programacoesFiltradas.length === 0 && (
                  <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
                    Nenhuma PG compatível encontrada. Você pode criá-la aqui pela opção “Criar nova PG”.
                  </div>
                )}
              </div>
            )}

            {requiresProgramacao && isNewProgramacao && (
              <div style={{
                display: "grid",
                gap: 10,
                padding: 12,
                borderRadius: 10,
                border: "1px solid rgba(249,115,22,0.35)",
                background: "rgba(249,115,22,0.08)",
              }}>
                <Input
                  label="Número da nova PG *"
                  placeholder="Ex.: 2026/58885"
                  value={newProgramacaoCodigo}
                  onChange={(event) => {
                    setNewProgramacaoCodigo(event.target.value);
                    setConfirmNewProgramacaoLink(false);
                  }}
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Input
                    type="date"
                    label="Dia *"
                    value={newProgramacaoData}
                    onChange={(event) => {
                      setNewProgramacaoData(event.target.value);
                      setConfirmNewProgramacaoLink(false);
                    }}
                  />
                  <Input
                    type="time"
                    label="Horário *"
                    value={newProgramacaoHorario}
                    onChange={(event) => {
                      setNewProgramacaoHorario(event.target.value);
                      setConfirmNewProgramacaoLink(false);
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.875rem', color: "var(--muted)", lineHeight: 1.5 }}>
                  A nova PG será criada e vinculada nesta confirmação, usando o trecho, destino, motorista e veículo desta van. O dia e o horário precisam ser compatíveis com a rota.
                </div>
              </div>
            )}

            {requiresProgramacao && (isNewProgramacao || isExistingProgramacaoUnlinked) && (
              <div style={{
                display: "grid",
                gap: 10,
                padding: 12,
                borderRadius: 10,
                border: "1px solid rgba(249,115,22,0.35)",
                background: "rgba(249,115,22,0.08)",
              }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: '0.9375rem', lineHeight: 1.45, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={confirmNewProgramacaoLink}
                    onChange={(event) => setConfirmNewProgramacaoLink(event.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    Confirmo que a PG <strong>{isNewProgramacao
                      ? newProgramacaoCodigo.trim() || "informada"
                      : programacaoMap.get(Number(selectedProgramacaoId))?.codigo || "selecionada"}</strong> deve ser {isNewProgramacao ? "criada e vinculada" : "vinculada"} a esta rota já disparada. Os funcionários marcados serão incluídos e o mapa será recalculado sem apagar respostas anteriores.
                  </span>
                </label>
              </div>
            )}

            {funcionariosLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <Spinner />
              </div>
            ) : (
              <>
                <Input
                  placeholder="Buscar funcionário por nome ou bairro..."
                  value={passengerSearch}
                  onChange={(e) => setPassengerSearch(e.target.value)}
                />
                <div style={{
                  maxHeight: 280,
                  overflowY: "auto",
                  display: "grid",
                  gap: 8,
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  padding: 10,
                  background: "var(--s2)",
                }}>
                  {funcionariosDisponiveis.length === 0 ? (
                    <div style={{ color: "var(--muted)", fontSize: '0.9375rem', padding: 8 }}>
                      Nenhum funcionário disponível para este grupo.
                    </div>
                  ) : funcionariosDisponiveis.map((funcionario) => {
                    const checked = selectedFuncionarioIds.has(Number(funcionario.id));
                    return (
                      <label
                        key={funcionario.id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          padding: "10px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          background: checked ? "rgba(249,115,22,0.12)" : "var(--s3)",
                          border: `1px solid ${checked ? "rgba(249,115,22,0.35)" : "transparent"}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePassengerSelection(funcionario.id)}
                          style={{ marginTop: 3 }}
                        />
                        <span style={{ fontSize: '1rem', lineHeight: 1.45 }}>
                          <strong>{funcionario.nome}</strong>
                          {funcionario.bairro ? ` — ${funcionario.bairro}` : ""}
                          {funcionario.cidade ? `, ${funcionario.cidade}` : ""}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {selecionados > 0 && (
                  <div style={{ fontSize: '0.9375rem', color: "var(--muted)" }}>
                    {selecionados} funcionário{selecionados !== 1 ? "s" : ""} selecionado{selecionados !== 1 ? "s" : ""}
                  </div>
                )}
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
              <Btn variant="secondary" onClick={() => setAddPassengerDialog(null)}>Cancelar</Btn>
              <Btn
                onClick={confirmAddPassenger}
                disabled={funcionariosLoading || selecionados === 0 || rowLoadingId === addPassengerDialog.route.id}
              >
                {rowLoadingId === addPassengerDialog.route.id
                  ? <><Spinner /> Adicionando</>
                  : selecionados > 1
                    ? aguardandoMotorista
                      ? `Adicionar ${selecionados} passageiros à rota`
                      : `Adicionar ${selecionados} passageiros e recalcular`
                    : aguardandoMotorista
                      ? "Adicionar passageiro à rota"
                      : "Adicionar e recalcular rota"}
              </Btn>
            </div>
          </div>
        </Modal>
        );
      })()}

      <ConfirmModal
        open={Boolean(removePassengerDialog)}
        title="Remover passageiro da rota"
        message={(
          <div style={{ display: "grid", gap: 10 }}>
            <p style={{ margin: 0 }}>
              Remover <strong>{removePassengerDialog?.person?.nome || "este passageiro"}</strong> desta van?
            </p>
            <p style={{ margin: 0, color: "var(--muted)", lineHeight: 1.5 }}>
              O passageiro não receberá nenhuma mensagem sobre a remoção. {getEnvioGrupo(
                removePassengerDialog?.route,
                removePassengerDialog?.groupIndex
              )?.motorista_respondeu
                ? "A rota será recalculada e somente o motorista receberá o novo mapa."
                : "Quando o motorista responder, o primeiro mapa já será gerado sem esse endereço."}
            </p>
          </div>
        )}
        cancelLabel="Cancelar"
        confirmLabel="Remover passageiro"
        confirmVariant="danger"
        loading={rowLoadingId === removePassengerDialog?.route?.id}
        onClose={() => {
          if (rowLoadingId === removePassengerDialog?.route?.id) return;
          setRemovePassengerDialog(null);
        }}
        onConfirm={confirmRemovePassenger}
      />

      <ConfirmModal
        open={Boolean(confirmDialog)}
        title={confirmDialog?.type === "deleteRoute"
          ? (canCancelProgramPlan(confirmDialog?.route)
            ? (hasDispatchStarted(confirmDialog?.route) ? "Cancelar rota disparada" : "Cancelar planejamento")
            : "Excluir rota")
          : confirmDialog?.retry
            ? "Tentar envio da rota novamente"
            : "Confirmar envio da rota"}
        message={confirmDialog?.type === "deleteRoute"
          ? (
            <div style={{ display: "grid", gap: 12 }}>
              <label htmlFor="route-delete-reason">Justificativa da exclusão (obrigatória)</label>
              <textarea
                id="route-delete-reason"
                value={confirmDialog?.justificativa || ""}
                onChange={event => setConfirmDialog(current => current ? { ...current, justificativa: event.target.value } : current)}
                minLength={5} maxLength={1000} rows={3} required
                placeholder="Explique por que esta rota está sendo excluída"
                style={{ width: "100%", background: "var(--s2)", color: "var(--text)", padding: 10, borderRadius: 8 }}
              />
              <small>A justificativa, seu usuário e a data ficarão registrados na auditoria. Mínimo de 5 caracteres.</small>
              {
            canCancelProgramPlan(confirmDialog?.route)
              ? (hasDispatchStarted(confirmDialog?.route)
                ? (
                  <div style={{ display: "grid", gap: 12 }}>
                    <p style={{ margin: 0 }}>
                      Esta ação encerrará a rota no RotaFlex e devolverá os pedidos para pendente. Mensagens já aceitas pela Meta não podem ser recolhidas e permanecerão no histórico; o motorista ainda poderá vê-las no WhatsApp.
                    </p>
                    <p style={{ margin: 0, color: "#fbbf24" }}>
                      Se for criar a rota corrigida, avise o motorista para considerar somente a mensagem mais recente.
                    </p>
                    <div>
                      <label htmlFor="route-delete-confirmation" style={{ display: "block", marginBottom: 5, color: "var(--text)", fontSize: '0.875rem', fontWeight: 700 }}>
                        Digite EXCLUIR para confirmar
                      </label>
                      <Input
                        id="route-delete-confirmation"
                        value={confirmDialog?.confirmationText || ""}
                        onChange={(event) => setConfirmDialog((current) => (
                          current ? { ...current, confirmationText: event.target.value } : current
                        ))}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                )
                : "Cancelar este planejamento? Os pedidos serão preservados e voltarão para pendente.")
              : (
                <div style={{ display: "grid", gap: 12 }}>
                  <p style={{ margin: 0 }}>
                    {isDeleteAfterResponse(confirmDialog?.route)
                      ? (confirmDialog?.route?.planejamento_origem_id
                        ? "Exclusão administrativa após resposta do motorista. Somente esta van, seus documentos e assinaturas serão removidos da operação. As demais vans e o planejamento serão preservados. Pedidos compartilhados continuam no fluxo; pedidos sem outra van vinculada ficam cancelados e não voltam para “A fazer”. Mensagens já aceitas pela Meta permanecem no WhatsApp e no histórico de auditoria."
                        : "Exclusão administrativa após resposta do motorista. A rota, seus documentos e assinaturas serão removidos da operação. Os pedidos de origem serão preservados como cancelados e não voltarão para “A fazer”. Mensagens já aceitas pela Meta não podem ser recolhidas: seus status e identificadores permanecerão no histórico de auditoria.")
                      : hasDispatchStarted(confirmDialog?.route) && !confirmDialog?.route?.tem_resposta_motorista
                      ? "Esta ação encerrará a van no RotaFlex. Mensagens já aceitas pela Meta não podem ser recolhidas e permanecerão no histórico; o motorista ainda poderá vê-las no WhatsApp."
                      : canEditDraft(confirmDialog?.route)
                        ? "Esta ação removerá a van em rascunho da operação."
                        : "Esta ação removerá definitivamente a rota, seus documentos e suas assinaturas."}
                    {" "}O histórico será preservado. A van deixará de aparecer em Pedidos.
                  </p>
                  <div>
                    <label htmlFor="route-delete-confirmation" style={{ display: "block", marginBottom: 5, color: "var(--text)", fontSize: '0.875rem', fontWeight: 700 }}>
                      Digite EXCLUIR para confirmar
                    </label>
                    <Input
                      id="route-delete-confirmation"
                      value={confirmDialog?.confirmationText || ""}
                      onChange={(event) => setConfirmDialog((current) => (
                        current ? { ...current, confirmationText: event.target.value } : current
                      ))}
                      autoComplete="off"
                    />
                  </div>
                </div>
              )
              }
            </div>
          )
          : (
            <div style={{ display: "grid", gap: 12 }}>
              <p style={{ fontWeight: 900 , color: "white" }}>
                Confira o motorista, o veículo e os passageiros desta van. As mensagens serão
                enfileiradas e a rota será confirmada somente depois que o canal registrar o envio.
                Passageiros poderão ser adicionados ou removidos até o motorista iniciar a rota.
              </p>
              {(confirmDialog?.route?.payload?.grupos || []).map((group, index) => (
                <Card key={`${group.regiao}-${index}`} style={{ padding: 16 }}>
                  <div style={{ fontSize: '0.9375rem', color: "white", fontWeight: 700, marginTop: 1 }}>
                    {group.programacao_codigo_responsavel
                      ? `Van responsável pelo pedido ${group.programacao_codigo_responsavel} · `
                      : ""}
                    Motorista: {group.motorista?.nome || "—"} · Veículo: {group.veiculo ? `${group.veiculo.veiculo} (${group.veiculo.placa})` : "—"}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: "#93c5fd", marginTop: 6 }}>
                    Horário desta van: {formatDate(group.data || confirmDialog?.route?.data)}
                    {" · "}{group.horario || confirmDialog?.route?.horario || "—"}
                  </div>
                  <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                    {(group.pessoas || []).map((person) => (
                      <div
                        key={person.programacao_passageiro_id || person.id || `${person.nome}-${person.telefone}`}
                        style={{ background: "var(--s3)", borderRadius: 8, padding: "7px 10px", fontSize: '0.9375rem', color: "white" }}
                      >
                        <strong>{person.nome}</strong> · {phoneToDisplay(person.telefone)}
                        {person.id_pedido_origem && (
                          <div style={{ color: "#93c5fd", fontSize: '0.8125rem', marginTop: 3 }}>
                            Pedido de origem: {person.id_pedido_origem}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )
        }
        confirmLabel={confirmDialog?.type === "deleteRoute"
          ? (canCancelProgramPlan(confirmDialog?.route)
            ? (hasDispatchStarted(confirmDialog?.route) ? "Cancelar rota disparada" : "Cancelar planejamento")
            : isDeleteAfterResponse(confirmDialog?.route)
              ? "Excluir definitivamente"
              : "Excluir")
          : confirmDialog?.retry
            ? "Tentar novamente"
            : "Revisar e enviar"}
        confirmVariant={confirmDialog?.type === "deleteRoute" ? "danger" : "primary"}
        confirmDisabled={Boolean(
          confirmDialog?.type === "deleteRoute"
          && (String(confirmDialog?.justificativa || "").trim().length < 5
            || (requiresDeleteConfirmationText(confirmDialog?.route)
              && String(confirmDialog?.confirmationText || "").trim().toUpperCase() !== "EXCLUIR"))
        )}
        loading={rowLoadingId === confirmDialog?.route?.id}
        onClose={() => {
          if (rowLoadingId === confirmDialog?.route?.id) return;
          setConfirmDialog(null);
        }}
        onConfirm={async () => {
          const current = confirmDialog;
          if (!current?.route) return;

          if (current.type === "deleteRoute") {
            if (
              requiresDeleteConfirmationText(current.route)
              && String(current.confirmationText || "").trim().toUpperCase() !== "EXCLUIR"
            ) {
              return;
            }
            if (String(current.justificativa || "").trim().length < 5) return;
            const deleted = await deleteRoute(current.route, current.justificativa);
            if (deleted) setConfirmDialog(null);
            return;
          }

          const result = await confirmRoute(current.route);
          if (result?.needsPhoneOverride) {
            recordUiError({
              message: "Alguns telefones não passaram na validação do WhatsApp.",
              code: "TELEFONES_WHATSAPP_INVALIDOS",
              page: "pedidos_operacao",
              source: "validation_dialog",
              details: { count: result.falhas?.length || 0 },
            });
            setConfirmDialog(null);
            setPhoneValidationDialog({
              route: current.route,
              falhas: result.falhas,
            });
            return;
          }
          if (result?.ok) {
            setConfirmDialog(null);
          }
        }}
      />

      <ConfirmModal
        open={Boolean(phoneValidationDialog)}
        title="Telefones com problema no WhatsApp"
        message={(
          <div style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              Alguns telefones não passaram na validação. A van continua salva para nova tentativa.
              Deseja enviar mesmo assim?
            </p>
            <div style={{ display: "grid", gap: 8 }}>
              {formatPhoneValidationFalhas(phoneValidationDialog?.falhas || []).map((linha) => (
                <div
                  key={linha}
                  style={{
                    fontSize: '0.9375rem',
                    color: "#fca5a5",
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    lineHeight: 1.45,
                  }}
                >
                  {linha}
                </div>
              ))}
            </div>
          </div>
        )}
        cancelLabel="Não"
        confirmLabel="Sim, enviar mesmo assim"
        confirmVariant="primary"
        loading={rowLoadingId === phoneValidationDialog?.route?.id}
        onClose={() => {
          if (rowLoadingId === phoneValidationDialog?.route?.id) return;
          setPhoneValidationDialog(null);
        }}
        onConfirm={confirmRouteIgnoringPhoneValidation}
      />

      {executorDialog && (
        <Modal
          title={`Trocar motorista ou veículo — Van ${executorDialog.route?.payload?.grupos?.[executorDialog.groupIndex]?.planejamento_van_numero || executorDialog.groupIndex + 1}`}
          onClose={() => { if (!savingExecutor) setExecutorDialog(null); }}
        >
          <div style={{ display: "grid", gap: 14 }}>
            {executorDialog.responsavelCodigo && (
              <div style={{ fontSize: '0.9375rem', color: "#93c5fd", lineHeight: 1.5 }}>
                O pedido responsável desta van é <strong>{executorDialog.responsavelCodigo}</strong>.
                A troca de motorista ou veículo mantém os passageiros e o pedido de origem.
              </div>
            )}
            <Select
              label="Motorista *"
              value={executorDialog.motoristaId}
              onChange={(event) => setExecutorDialog((current) => ({ ...current, motoristaId: event.target.value }))}
            >
              <option value="">Selecione...</option>
              {motoristas.map((motorista) => (
                <option key={motorista.id} value={motorista.id}>{motorista.nome}</option>
              ))}
            </Select>
            <Select
              label="Veículo *"
              value={executorDialog.veiculoId}
              onChange={(event) => setExecutorDialog((current) => ({ ...current, veiculoId: event.target.value }))}
            >
              <option value="">Selecione...</option>
              {veiculos.map((veiculo) => (
                <option key={veiculo.id} value={veiculo.id}>
                  {veiculo.veiculo} ({veiculo.placa}) · {veiculo.capacidade || 15} lugares
                </option>
              ))}
            </Select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn variant="secondary" onClick={() => setExecutorDialog(null)} disabled={savingExecutor}>Cancelar</Btn>
              <Btn onClick={saveExecutor} disabled={savingExecutor}>
                {savingExecutor ? <><Spinner /> Salvando...</> : "Salvar motorista e veículo"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <Toast onDismiss={() => setToast(null)} data-testid="operation-toast" tone={toast.type === "error" ? "error" : "success"}>{toast.message}</Toast>
      )}
    </div>
  );
}
